import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from './supabase';

/* ============================================================
 *  ARCANE PRO — DASHBOARD v2
 *  Layout: Aura Focus Scene (slate-900/950, 3 colunas, aura-ring)
 *  Accent: roxo Arcane (#6366F1)
 *  ------------------------------------------------------------
 *  PRESERVA 100% da lógica de negócio do App.js original:
 *  - 6 módulos × 23 ferramentas
 *  - Upload routes (/modulos/financeiro/analisar, /modulos/juridico/analisar)
 *  - Texto route (/modulos/texto/gerar) + formatarResultadoModulo
 *  - Controle de plano (canUse, PLAN_ORDER)
 *  - Limites mensais (profiles.generations_used)
 *  - Histórico em supabase.history
 *  - CompanyProfileModal + ProfileEditor (passados via props)
 *  - Toast
 *
 *  INTEGRAÇÃO: veja comentário no FINAL do arquivo.
 * ============================================================ */

// ══════════════ CONSTANTES (sync com App.js) ══════════════
const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || 'https://arcanepro-production.up.railway.app').replace(/\/$/, '');

const SECTORS = ['Varejo', 'Serviços', 'Tecnologia', 'Saúde', 'Educação', 'Construção', 'Alimentação', 'Agronegócio', 'Logística', 'Consultoria', 'Jurídico', 'Contabilidade', 'Marketing / Agência', 'Outro'];
const SIZES = ['MEI / Autônomo', 'Microempresa (2-9 funcionários)', 'Pequena empresa (10-49 funcionários)', 'Média empresa (50+ funcionários)'];
const TONES = ['Formal e técnico', 'Neutro e profissional', 'Descontraído e próximo'];

const UPLOAD_ROUTES = {
  analisar_planilha: '/modulos/financeiro/analisar',
  revisar_contrato: '/modulos/juridico/analisar',
};

const MODULES = [
  { id: 'diagnostico_financeiro', icon: '◎', label: 'Financeiro', plan: 'essencial', color: '#fbbf24', desc: 'Visão estratégica, previsibilidade e controle absoluto do seu caixa.',
    tools: [
      { id: 'analisar_planilha', name: 'Analisar Planilha / Extrato', desc: 'Envie seus dados e receba diagnóstico completo', upload: true },
      { id: 'fluxo_caixa_diagnostico', name: 'Diagnosticar Fluxo de Caixa', desc: 'Identifique riscos e gaps no seu caixa' },
      { id: 'identificar_desperdicio', name: 'Identificar Desperdícios', desc: 'Onde você está perdendo dinheiro' },
      { id: 'projecao_cenarios', name: 'Projeção de Cenários', desc: 'Pessimista, realista e otimista para 6 meses' },
    ] },
  { id: 'juridico', icon: '⬡', label: 'Jurídico', plan: 'essencial', color: '#fb923c', desc: 'Governança, conformidade e auditoria de contratos com precisão Arcane.',
    tools: [
      { id: 'revisar_contrato', name: 'Revisar Contrato', desc: 'Identifique riscos antes de assinar', upload: true },
      { id: 'gerar_contrato', name: 'Gerar Contrato', desc: 'Contrato profissional em minutos' },
      { id: 'lgpd_diagnostico', name: 'Diagnóstico LGPD', desc: 'Nível de conformidade e plano de adequação' },
      { id: 'nda', name: 'Acordo de Confidencialidade', desc: 'NDA robusto para proteger seu negócio' },
    ] },
  { id: 'rh', icon: '⬟', label: 'RH & Pessoas', plan: 'essencial', color: '#e879f9', desc: 'Inteligência em gestão de talentos, performance e cultura.',
    tools: [
      { id: 'diagnostico_time', name: 'Diagnosticar o Time', desc: 'Riscos, gaps e plano de retenção' },
      { id: 'descricao_vaga', name: 'Criar Descrição de Vaga', desc: 'Atraia os candidatos certos' },
      { id: 'avaliacao_desempenho', name: 'Avaliação de Desempenho', desc: 'Formulário estruturado com plano de metas' },
      { id: 'plano_onboarding', name: 'Plano de Onboarding', desc: 'Integre novos colaboradores com eficiência' },
      { id: 'politica_interna', name: 'Política Interna', desc: 'Regras claras que alinham a equipe' },
    ] },
  { id: 'marketing', icon: '◆', label: 'Marketing Digital', plan: 'essencial', color: '#f472b6', desc: 'Máquina de aquisição in-house orientada a dados e conversão.',
    tools: [
      { id: 'diagnostico_marketing', name: 'Diagnosticar Marketing', desc: 'Gaps, oportunidades e estratégia de 90 dias' },
      { id: 'estrategia_conteudo', name: 'Estratégia de Conteúdo', desc: 'Plano editorial de 30 dias por canal' },
      { id: 'copy_vendas', name: 'Copy de Vendas', desc: 'Textos que convertem para produto ou serviço' },
      { id: 'post_redes_sociais', name: 'Posts para Redes Sociais', desc: 'LinkedIn, Instagram e WhatsApp prontos' },
    ] },
  { id: 'operacoes', icon: '◈', label: 'Operações', plan: 'essencial', color: '#38bdf8', desc: 'Padronização, automação de processos e eficiência em escala.',
    tools: [
      { id: 'diagnostico_processos', name: 'Diagnosticar Processos', desc: 'Gargalos, desperdícios e mapa do processo ideal' },
      { id: 'proposta_comercial', name: 'Proposta Comercial', desc: 'Proposta consultiva que fecha negócio' },
      { id: 'ata_reuniao', name: 'Ata de Reunião', desc: 'Decisões e tarefas documentadas com precisão' },
      { id: 'email_profissional', name: 'E-mail Profissional', desc: 'Comunicação corporativa clara e eficaz' },
    ] },
  { id: 'consultoria', icon: '★', label: 'Consultoria Estratégica', plan: 'profissional', color: '#a78bfa', desc: 'Consultoria de alto nível para decisões críticas',
    tools: [
      { id: 'diagnostico_completo', name: 'Diagnóstico Completo do Negócio', desc: 'Visão 360° com prioridades e roteiro de 90 dias' },
      { id: 'plano_crescimento', name: 'Plano de Crescimento', desc: 'Estratégia para dobrar o negócio em 12 meses' },
      { id: 'analise_concorrentes', name: 'Análise de Concorrentes', desc: 'Inteligência competitiva e posicionamento' },
    ] },
];

const TOOL_TIME = {
  analisar_planilha: 180, fluxo_caixa_diagnostico: 120, identificar_desperdicio: 90, projecao_cenarios: 150,
  revisar_contrato: 180, gerar_contrato: 120, lgpd_diagnostico: 240, nda: 90,
  diagnostico_time: 120, descricao_vaga: 60, avaliacao_desempenho: 90, plano_onboarding: 120, politica_interna: 150,
  diagnostico_marketing: 120, estrategia_conteudo: 90, copy_vendas: 60, post_redes_sociais: 45,
  diagnostico_processos: 150, proposta_comercial: 90, ata_reuniao: 30, email_profissional: 20,
  diagnostico_completo: 300, plano_crescimento: 240, analise_concorrentes: 180,
};
const HOURLY_RATE = 80;

// Guias de uso por ferramenta — placeholder específico, dicas (bullets) e exemplo pronto
const TOOL_GUIDES = {
  // ─── FINANCEIRO ───
  analisar_planilha: {
    placeholder: 'Conte sobre a planilha que está enviando. Ex: período coberto, o que considera receita/despesa, tipo de negócio...',
    dicas: [
      'Período coberto pelos dados (mês? trimestre?)',
      'O que cada coluna representa',
      'Tipo de negócio e ticket médio',
      'O que você quer descobrir com a análise',
    ],
    exemplo: 'Estou enviando o extrato consolidado de Mar/2026 da minha clínica de estética. As colunas são data, descrição, valor (positivo = receita, negativo = despesa) e categoria. Faturamento médio mensal R$ 45k, ticket médio R$ 280. Quero entender onde estou sangrando dinheiro e quais despesas posso cortar sem afetar o atendimento.',
  },
  fluxo_caixa_diagnostico: {
    placeholder: 'Descreva como o dinheiro entra e sai do seu negócio hoje. Ex: prazo de recebimento, principais despesas, sazonalidade...',
    dicas: [
      'Receita média mensal (faixa)',
      'Prazo médio de recebimento dos clientes',
      'Suas 3 maiores despesas fixas',
      'Sazonalidade ou irregularidade no fluxo',
    ],
    exemplo: 'Minha agência de marketing fatura entre R$ 30k-50k/mês, depende dos projetos. Recebo metade na assinatura do contrato e metade após 30 dias. Maiores despesas: salários (R$ 18k), aluguel + estrutura (R$ 4k), tráfego pago de teste (R$ 3k). Tenho dificuldade em pagar conta no fim do mês mesmo com cliente devendo R$ 25k.',
  },
  identificar_desperdicio: {
    placeholder: 'Liste os principais gastos da empresa e onde você desconfia que tem desperdício...',
    dicas: [
      'Faturamento mensal aproximado',
      'Maiores categorias de despesa',
      'Onde você suspeita de gasto desnecessário',
      'Já tentou cortar algo? O que aconteceu?',
    ],
    exemplo: 'Restaurante familiar com faturamento R$ 80k/mês. Minhas categorias: insumos (35%), pessoal (28%), aluguel + contas (15%), delivery/marketplace (12%), demais (10%). Acho que tem muito desperdício em insumo (sobra muita comida) e nas taxas dos apps (iFood, Rappi). Já reduzi cardápio mas não vi efeito.',
  },
  projecao_cenarios: {
    placeholder: 'Descreva sua receita atual, principais custos e o que você espera dos próximos 6 meses...',
    dicas: [
      'Receita média dos últimos 3-6 meses',
      'Custos fixos e variáveis principais',
      'Mudanças previstas (contratação, expansão, perda de cliente grande)',
      'Sazonalidade do seu setor',
    ],
    exemplo: 'Empresa de TI com 5 funcionários, faturamento médio R$ 60k/mês nos últimos 6 meses. Custos fixos R$ 38k (salários, aluguel, software). Em maio perco um cliente que representa 25% da receita. Quero saber como ficam os 6 meses seguintes nos cenários pessimista, realista e otimista.',
  },

  // ─── JURÍDICO ───
  revisar_contrato: {
    placeholder: 'Conte o contexto do contrato. Ex: qual é o seu papel, o que está em jogo, suas dúvidas principais...',
    dicas: [
      'Você é o contratante ou o contratado?',
      'Tipo de contrato (prestação de serviço, locação, etc.)',
      'Valor envolvido e duração',
      'Quais cláusulas mais te preocupam',
    ],
    exemplo: 'Sou o contratado. Vou prestar serviço de consultoria de marketing pra uma rede de farmácias por 12 meses, R$ 8.000/mês. Eles me mandaram esse contrato. Quero saber se tem cláusula abusiva, se a multa de rescisão tá justa e se a exclusividade exigida é razoável.',
  },
  gerar_contrato: {
    placeholder: 'Descreva o contrato que precisa: partes envolvidas, objeto, valor, prazo e cláusulas específicas...',
    dicas: [
      'Quem são as partes (nome, CNPJ se tiver)',
      'Qual o serviço ou produto contratado',
      'Valor e forma de pagamento',
      'Prazo (vigência, renovação)',
      'Cláusulas especiais (multa, foro, exclusividade)',
    ],
    exemplo: 'Preciso de contrato de prestação de serviço entre meu studio (Studio Criativo Ltda, CNPJ 12.345.678/0001-90) e cliente Padaria Bom Sabor. Serviço: gestão de redes sociais (Instagram + Facebook), 6 meses, R$ 2.500/mês com pagamento até dia 5. Multa rescisória de 20% do saldo restante. Foro de São José dos Campos.',
  },
  lgpd_diagnostico: {
    placeholder: 'Descreva como sua empresa coleta, usa e armazena dados de clientes hoje...',
    dicas: [
      'Que dados de clientes você coleta (nome, CPF, telefone, e-mail, etc.)',
      'Onde armazena (Excel? CRM? sistema próprio?)',
      'Quem na empresa tem acesso',
      'Já teve algum incidente ou pedido de exclusão de dados',
    ],
    exemplo: 'Loja virtual de roupas com ~3.000 cadastros. Coleto nome, CPF, endereço, e-mail, histórico de compras. Armazeno no CRM RD Station + planilha Excel pro time comercial. 4 funcionários têm acesso. Nunca tive pedido de exclusão. Quero saber meu nível de conformidade e o que falta fazer pra ficar adequado.',
  },
  nda: {
    placeholder: 'Conte o contexto do NDA: com quem, sobre o que e qual o objetivo da confidencialidade...',
    dicas: [
      'Quem são as partes (você e quem?)',
      'O que precisa ficar confidencial (produto, estratégia, lista de clientes...)',
      'Duração do compromisso',
      'É bilateral ou só uma parte protege?',
    ],
    exemplo: 'Vou apresentar minha ideia de produto SaaS pra um possível investidor. Preciso de NDA bilateral entre minha empresa (Arcane Ltda) e o investidor (pessoa física, CPF a preencher). Confidencial: arquitetura técnica, roadmap de produto, dados de tração. Validade 3 anos após a reunião. Foro de São Paulo/SP.',
  },

  // ─── RH ───
  diagnostico_time: {
    placeholder: 'Descreva seu time atual: quantas pessoas, papéis, problemas que você percebe...',
    dicas: [
      'Tamanho do time e principais cargos',
      'Tempo médio de casa',
      'Onde você sente que tem gargalo (pessoa sobrecarregada, falta de skill, etc.)',
      'Última saída/entrada relevante',
    ],
    exemplo: 'Agência de marketing, 8 pessoas: eu (sócio/atendimento), 2 designers, 2 redatores, 1 gestor de tráfego, 1 social media, 1 financeiro. O gestor de tráfego está sobrecarregado (atende sozinho 12 contas). Designer pleno saiu mês passado e ainda não repus. Sinto que falta alguém estratégico.',
  },
  descricao_vaga: {
    placeholder: 'Conte a vaga: cargo, nível, principais atividades e o perfil que você busca...',
    dicas: [
      'Cargo e nível (júnior/pleno/sênior)',
      'Principais atividades do dia a dia',
      'Skills técnicas obrigatórias',
      'Perfil comportamental que combina com o time',
      'Faixa salarial e modelo (CLT, PJ, remoto/híbrido)',
    ],
    exemplo: 'Procuro Designer Pleno pra agência. Atividades: criar peças pra redes sociais, identidade visual de novos clientes, suporte ao time de social media. Obrigatório: Figma, Photoshop, portfólio com mín. 2 anos. Perfil: organizado, autônomo, gosta de variedade. CLT R$ 4.500 + benefícios, híbrido (3x/semana presencial em SJC/SP).',
  },
  avaliacao_desempenho: {
    placeholder: 'Descreva o colaborador a ser avaliado: cargo, tempo de casa, principais responsabilidades e como tem performado...',
    dicas: [
      'Cargo e tempo de casa',
      'Principais entregas/responsabilidades',
      'O que está indo bem',
      'Onde você vê espaço pra melhorar',
      'Objetivos pra próximos 3-6 meses',
    ],
    exemplo: 'João, gestor de tráfego, 1 ano e meio na agência. Responsável por 12 contas (R$ 200k/mês de mídia). Entrega bem o operacional, ROI médio 4.2x. Pontos a melhorar: comunicação com cliente (reclamações de relatórios atrasados), pouco proativo em propor novos testes. Quero traçar plano pra ele virar coordenador em 6 meses.',
  },
  plano_onboarding: {
    placeholder: 'Conte o cargo do novo colaborador, o que ele precisa aprender e quem vai ajudar na integração...',
    dicas: [
      'Cargo da nova contratação',
      'Conhecimentos prévios que ele já traz',
      'O que precisa aprender sobre a empresa',
      'Quem do time vai ser referência/buddy',
      'Quanto tempo você quer pro onboarding',
    ],
    exemplo: 'Contratei novo Social Media Pleno. Já tem 3 anos de experiência em outras agências. Precisa aprender: nossos clientes (8 ativos), processo interno de aprovação, ferramentas que usamos (mLabs, Canva Pro, Notion). Buddy: a Camila (social senior). Quero 30 dias de onboarding com entregas progressivas.',
  },
  politica_interna: {
    placeholder: 'Diga qual política precisa criar (home office, férias, código de conduta...) e o contexto da empresa...',
    dicas: [
      'Que política você quer formalizar',
      'Tamanho da empresa e modelo de trabalho',
      'O que motivou criar essa política agora',
      'Pontos sensíveis que precisam ficar claros',
    ],
    exemplo: 'Quero criar política de home office. Empresa tem 12 funcionários, modelo híbrido (3x escritório, 2x casa). Estamos formalizando porque virou bagunça (gente sumindo, cliente reclama). Sensível: equipamento (custeio próprio ou empresa), horário fixo, reuniões obrigatórias presenciais.',
  },

  // ─── MARKETING ───
  diagnostico_marketing: {
    placeholder: 'Conte sobre seu negócio: o que vende, pra quem, canais que usa hoje e principais dores de marketing...',
    dicas: [
      'O que vende e ticket médio',
      'Quem é o cliente ideal',
      'Canais que usa hoje (Instagram, Google Ads, indicação...)',
      'Principal dor (poucos leads? leads ruins? não converte?)',
      'Quanto investe em marketing/mês',
    ],
    exemplo: 'Vendo serviço de contabilidade pra empresas pequenas (faturamento até R$ 500k/ano). Ticket médio R$ 600/mês. Hoje 80% dos clientes vêm de indicação, 20% do Instagram orgânico. Investimento zero em pago. Dor: dependo demais de indicação, quero crescer mas não sei onde investir. Sou eu + 2 contadoras.',
  },
  estrategia_conteudo: {
    placeholder: 'Descreva seu negócio, público e onde quer publicar (Instagram, LinkedIn, blog...)...',
    dicas: [
      'Nicho de atuação',
      'Persona / cliente ideal',
      'Canal principal pra postar',
      'Tom de voz desejado (sério, descontraído, técnico...)',
      'Frequência que consegue manter',
    ],
    exemplo: 'Sou advogado trabalhista atendendo PJ. Persona: dono de PME com 10-50 funcionários. Quero postar no Instagram + LinkedIn. Tom profissional mas próximo (nada engessado). Consigo manter 3 posts/semana. Quero atrair empresários pra agendar consultoria preventiva.',
  },
  copy_vendas: {
    placeholder: 'Descreva o produto/serviço, pra quem é, principais benefícios e a oferta (preço, condições)...',
    dicas: [
      'O que é o produto/serviço',
      'Quem é o público-alvo',
      'Principal dor que resolve',
      'Diferenciais vs concorrência',
      'Oferta (preço, prazo, bônus)',
    ],
    exemplo: 'Curso online de gestão financeira pra donos de pequena empresa. Público: empresário 30-50 anos que sente que não controla o caixa. Resolve: planilha pronta + método em 4 semanas. Diferencial: aulas práticas (não teoria), professor com experiência real (não só certificado). Oferta: R$ 397 à vista ou 12x R$ 39, com 30 dias de garantia.',
  },
  post_redes_sociais: {
    placeholder: 'Conte o tema do post, em qual rede vai publicar e qual o objetivo (engajar, vender, educar)...',
    dicas: [
      'Tema do post',
      'Rede social (Instagram, LinkedIn, WhatsApp Status)',
      'Objetivo (educar, gerar lead, vender)',
      'Tom desejado',
      'Tem CTA específico?',
    ],
    exemplo: 'Tema: "5 erros que matam o caixa de pequenas empresas". Rede: Instagram (carrossel). Objetivo: educar e gerar interesse pra meu serviço de consultoria. Tom: direto, com exemplos reais. CTA: "Comenta AJUDA que mando o material completo".',
  },

  // ─── OPERAÇÕES ───
  diagnostico_processos: {
    placeholder: 'Descreva o processo atual que tem dor: como funciona hoje, onde trava, quem está envolvido...',
    dicas: [
      'Qual processo você quer melhorar',
      'Como funciona hoje (passo a passo curto)',
      'Onde trava ou demora',
      'Quem está envolvido',
      'Volume (quantas vezes por dia/semana?)',
    ],
    exemplo: 'Processo: aprovação de orçamento pra cliente novo. Hoje: comercial faz orçamento → manda pro sócio aprovar via WhatsApp → sócio responde quando vê → comercial manda pro cliente. Trava no sócio (demora 1-3 dias pra responder). Volume: ~15 orçamentos/semana. Cliente reclama da demora.',
  },
  proposta_comercial: {
    placeholder: 'Conte o cliente, o problema dele, sua solução e a oferta (escopo, prazo, valor)...',
    dicas: [
      'Quem é o cliente (empresa, setor, tamanho)',
      'Qual a dor dele',
      'O que você está propondo',
      'Escopo (o que entra e o que NÃO entra)',
      'Prazo e valor',
    ],
    exemplo: 'Cliente: rede de 4 padarias em SJC. Dor: vendas estagnadas, não sabem usar redes sociais. Proposta: gestão de Instagram + Facebook das 4 unidades por 6 meses. Escopo: 12 posts/mês por unidade, 2 reuniões mensais, relatório consolidado. Não entra: tráfego pago, criação de site. Investimento: R$ 4.500/mês.',
  },
  ata_reuniao: {
    placeholder: 'Descreva a reunião: assunto, participantes, principais decisões e tarefas geradas...',
    dicas: [
      'Data e tipo da reunião',
      'Participantes',
      'Principais assuntos discutidos',
      'Decisões tomadas',
      'Tarefas atribuídas (quem faz o quê e até quando)',
    ],
    exemplo: 'Reunião de planejamento Q2/2026, 27/Abr, 14h-15h30. Participantes: eu (CEO), Maria (financeiro), Pedro (comercial). Discutimos: meta do trimestre, contratação de SDR, redução de custos com software. Decisões: meta R$ 180k, contratar até 15/Mai, revisar contratos de software (corte mín. 15%). Tarefas: Pedro (descrição vaga - 30/Abr), Maria (mapeamento softwares - 05/Mai).',
  },
  email_profissional: {
    placeholder: 'Descreva pra quem é o e-mail, qual o objetivo e o tom desejado...',
    dicas: [
      'Pra quem é (cliente, fornecedor, parceiro...)',
      'Contexto da relação',
      'Objetivo do e-mail (cobrar, propor, declinar, agradecer...)',
      'Tom (formal, neutro, próximo)',
      'Algum ponto que precisa entrar',
    ],
    exemplo: 'Pra: cliente que está atrasando pagamento há 15 dias. Relação: cliente novo, primeira fatura. Objetivo: cobrar de forma firme mas sem queimar a ponte. Tom: formal mas humano. Precisa: lembrar do prazo no contrato, oferecer ajuda se for problema pontual, deixar claro que vai pausar serviço se passar de 30 dias.',
  },

  // ─── CONSULTORIA ESTRATÉGICA ───
  diagnostico_completo: {
    placeholder: 'Descreva sua empresa em 360°: o que vende, time, finanças, principais desafios e ambições...',
    dicas: [
      'O que a empresa faz e há quanto tempo',
      'Faturamento médio e margem aproximada',
      'Tamanho do time e estrutura',
      'Principais clientes / canais de aquisição',
      'Maiores desafios atuais',
      'Aonde você quer chegar (12-24 meses)',
    ],
    exemplo: 'Agência de marketing há 4 anos em SJC/SP. Faturamento R$ 80k/mês, margem ~25%. Time: 8 pessoas. 12 clientes ativos, 70% vêm de indicação. Desafios: dependência de poucos clientes (top 3 = 50% da receita), dificuldade em precificar projetos grandes, sócio (eu) faz tudo. Quero chegar a R$ 200k/mês em 18 meses sem virar moedor.',
  },
  plano_crescimento: {
    placeholder: 'Conte sua receita atual, o que funciona hoje e até onde você quer chegar...',
    dicas: [
      'Receita atual e dos últimos 12 meses',
      'O que está funcionando bem hoje',
      'Onde está travando o crescimento',
      'Recursos disponíveis (caixa, time, parcerias)',
      'Meta de crescimento (em quanto tempo?)',
    ],
    exemplo: 'Loja de suplementos online, faturamento médio R$ 50k/mês há 6 meses (cresceu de R$ 30k pra cá). Funciona bem: tráfego pago Meta com ROI 3.5x. Trava: estoque (preciso de mais capital de giro), só eu opero. Tenho R$ 40k em caixa. Quero dobrar pra R$ 100k/mês em 12 meses.',
  },
  analise_concorrentes: {
    placeholder: 'Liste 2-3 concorrentes que você considera principais e o que sabe sobre eles...',
    dicas: [
      'Seu negócio (o que vende, pra quem)',
      '2-3 concorrentes principais (nomes, instagrams ou sites)',
      'O que você acha que eles fazem melhor',
      'O que você acha que faz melhor',
      'Onde você sente que está perdendo pra eles',
    ],
    exemplo: 'Tenho clínica de odontologia em SJC, foco em estética dental. Concorrentes principais: Sorrisus (cadeia maior, várias unidades) e Dra. Carla Estetic (boutique, foco em alta renda). Sorrisus ganha em preço e mídia paga. Dra Carla ganha em posicionamento premium no Instagram (16k seguidores). Eu tenho equipamento melhor que ambas mas perco em marketing.',
  },
};

const PLANS = [
  { id: 'essencial', name: 'Essencial', price: 'R$ 127', period: '/mês', limit: 65, highlight: false,
    features: ['65 análises/mês', 'Todos os módulos básicos', 'Módulo Jurídico', 'Histórico completo', 'Suporte prioritário'] },
  { id: 'profissional', name: 'Profissional', price: 'R$ 297', period: '/mês', limit: 200, highlight: true,
    features: ['200 análises/mês', 'Todos os módulos', 'Consultoria Estratégica', 'Diagnóstico Completo do Negócio', 'Suporte dedicado'] },
  { id: 'gestao', name: 'Enterprise', price: 'Sob consulta', period: '', limit: 700, highlight: false,
    features: ['700 análises/mês', 'Tudo do Profissional', 'API de integração', 'SLA garantido', 'Onboarding personalizado'] },
];

const PLAN_ORDER = { free: 0, essencial: 1, starter: 1, profissional: 2, business: 2, gestao: 3, unlimited: 3 };

const KIRVANO_LINKS = {
  essencial: process.env.REACT_APP_KIRVANO_ESSENCIAL || 'https://pay.kirvano.com/SEU-LINK-ESSENCIAL',
  profissional: process.env.REACT_APP_KIRVANO_PROFISSIONAL || 'https://pay.kirvano.com/SEU-LINK-PROFISSIONAL',
  gestao: process.env.REACT_APP_KIRVANO_GESTAO || 'https://pay.kirvano.com/SEU-LINK-GESTAO',
};
const MP_LINKS = KIRVANO_LINKS;

// Mailto pré-preenchido para o plano Enterprise (lead qualificado)
const ENTERPRISE_EMAIL = 'contato@arcanepro.com.br';
const ENTERPRISE_MAILTO = (() => {
  const subject = 'Interesse no plano Enterprise — Arcane Pro';
  const body = `Olá Gabriel!

Tenho interesse no plano Enterprise do Arcane Pro. Segue abaixo minhas informações:

📋 SOBRE A EMPRESA
- Nome da empresa: 
- Site / Instagram: 
- Setor / segmento: 
- Cidade / Estado: 
- Número de funcionários: 
- Faturamento mensal aproximado: 

🎯 SOBRE A NECESSIDADE
- Principal desafio que estamos enfrentando: 
- Quantos usuários precisariam ter acesso: 
- Integrações desejadas (Bling, Omie, ContaAzul, outras): 

📞 CONTATO
- Nome: 
- Cargo: 
- Telefone / WhatsApp: 
- Melhor horário pra contato: 

[Enviado pelo botão "Falar com vendas" do Arcane Pro]`;
  return `mailto:${ENTERPRISE_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
})();

// ══════════════ HELPERS ══════════════
function canUse(userPlan, reqPlan) { return (PLAN_ORDER[userPlan] || 0) >= (PLAN_ORDER[reqPlan] || 0); }

function calcSaved(history) {
  const secs = history.reduce((acc, h) => acc + (TOOL_TIME[h.tool] || 60), 0);
  const hours = Math.round((secs / 60) * 10) / 10;
  const value = Math.round(hours * HOURLY_RATE);
  return { hours, value };
}

function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result || '');
    r.onerror = reject;
    r.readAsText(file);
  });
}

function formatarResultadoModulo(modulo, analise, dados) {
  const linhas = [];
  const push = (t) => linhas.push(t);
  const title = (t) => { push(''); push(`### ${t}`); push(''); };
  if (modulo === 'financeiro') {
    title('Análise Financeira');
    if (analise.diagnostico) push(analise.diagnostico);
    if (dados.receita_total != null) push(`\n**Receita total:** R$ ${Number(dados.receita_total).toLocaleString('pt-BR')}`);
    if (dados.despesa_total != null) push(`**Despesa total:** R$ ${Number(dados.despesa_total).toLocaleString('pt-BR')}`);
    if (dados.saldo != null) push(`**Saldo:** R$ ${Number(dados.saldo).toLocaleString('pt-BR')}`);
    if (analise.riscos?.length) { title('Riscos identificados'); analise.riscos.forEach((r) => push(`• ${r}`)); }
    if (analise.recomendacoes?.length) { title('Recomendações'); analise.recomendacoes.forEach((r) => push(`• ${r}`)); }
  } else if (modulo === 'juridico') {
    title('Revisão Jurídica');
    if (analise.resumo) push(analise.resumo);
    if (analise.riscos?.length) { title('Riscos encontrados'); analise.riscos.forEach((r) => push(`• ${r}`)); }
    if (analise.clausulas_atencao?.length) { title('Cláusulas que exigem atenção'); analise.clausulas_atencao.forEach((c) => push(`• ${c}`)); }
    if (analise.recomendacoes?.length) { title('Recomendações'); analise.recomendacoes.forEach((r) => push(`• ${r}`)); }
  } else if (modulo === 'rh') {
    title('Diagnóstico de RH');
    if (analise.resumo) push(analise.resumo);
    if (analise.riscos?.length) { title('Riscos'); analise.riscos.forEach((r) => push(`• ${r}`)); }
    if (analise.acoes?.length) { title('Ações recomendadas'); analise.acoes.forEach((a) => push(`• ${a}`)); }
  } else if (modulo === 'marketing') {
    title('Análise de Marketing');
    if (analise.resumo) push(analise.resumo);
    if (analise.oportunidades?.length) { title('Oportunidades'); analise.oportunidades.forEach((o) => push(`• ${o}`)); }
    if (analise.acoes?.length) { title('Ações 90 dias'); analise.acoes.forEach((a) => push(`• ${a}`)); }
  } else {
    if (typeof analise === 'string') push(analise);
    else push(JSON.stringify(analise, null, 2));
  }
  return linhas.join('\n');
}

function formatRelative(iso) {
  try {
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'agora';
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}d`;
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
  } catch { return ''; }
}

// ══════════════ CSS (embutido, sem Tailwind) ══════════════
const DASH_CSS = `
/* Reset global pra eliminar margin/padding padrao do body */
html, body, #root { margin: 0 !important; padding: 0 !important; background: #030303; }

.arcane-dash {
  --arcane: #6366F1;
  --arcane-glow: rgba(99,102,241,0.35);
  --bg: linear-gradient(to bottom, #030303, #0a0a0a);
  --panel: rgba(15,23,42,0.7);
  --panel-inner: rgba(2,6,23,0.6);
  --border: rgba(51,65,85,0.6);
  --border-soft: rgba(30,41,59,0.8);
  --text: #f1f5f9;
  --text-muted: #94a3b8;
  --text-dim: #64748b;
  font-family: 'Inter', system-ui, -apple-system, sans-serif;
  min-height: 100vh;
  width: 100%;
}
.arcane-dash *, .arcane-dash *::before, .arcane-dash *::after { box-sizing: border-box; }
.arcane-dash button { font-family: inherit; cursor: pointer; }
.arcane-dash input, .arcane-dash textarea { font-family: inherit; }

.ad-root {
  min-height: 100vh;
  background: var(--bg);
  color: var(--text);
  position: relative;
  overflow-x: hidden;
}
.ad-root::before {
  content: ''; position: fixed; inset: 0; pointer-events: none; z-index: 0;
  background:
    radial-gradient(ellipse 60% 40% at 15% 0%, rgba(99,102,241,0.10), transparent 60%),
    radial-gradient(ellipse 60% 40% at 85% 100%, rgba(99,102,241,0.06), transparent 60%);
}
.ad-frame {
  position: relative; z-index: 1;
  min-height: 100vh;
  background: linear-gradient(to bottom, rgba(15,23,42,0.6), rgba(2,6,23,0.6));
  display: flex; flex-direction: column;
}
.ad-topbar {
  display: flex; align-items: center; justify-content: space-between;
  padding: 10px 20px;
  border-bottom: 1px solid var(--border-soft);
  background: rgba(2,6,23,0.85);
  backdrop-filter: blur(12px);
  position: sticky; top: 0; z-index: 20;
  height: 54px;
}
.ad-traffic { display: inline-flex; gap: 6px; }
.ad-dot { width: 10px; height: 10px; border-radius: 50%; }
.ad-dot-r { background: rgba(244,63,94,0.8); }
.ad-dot-a { background: rgba(251,191,36,0.8); }
.ad-dot-g { background: rgba(16,185,129,0.8); }
.ad-brand-pill {
  margin-left: 12px; display: inline-flex; align-items: center; gap: 8px;
  border: 1px solid var(--border-soft); background: var(--panel);
  padding: 6px 12px; border-radius: 10px; font-size: 11px; color: var(--text);
}
.ad-brand-square {
  width: 16px; height: 16px; border-radius: 4px;
  display: inline-flex; align-items: center; justify-content: center;
  background: var(--arcane); color: #fff;
}
.ad-topbar-actions { display: flex; gap: 8px; align-items: center; }
.ad-chip-muted {
  display: inline-flex; align-items: center;
  border: 1px solid var(--border-soft); background: var(--panel);
  padding: 4px 10px; border-radius: 8px;
  font-size: 10px; letter-spacing: 0.15em; text-transform: uppercase;
  color: var(--text-muted);
}
.ad-btn-primary {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 6px 14px; border-radius: 8px; border: none;
  background: var(--arcane); color: #fff;
  font-size: 11px; font-weight: 600;
  box-shadow: 0 0 20px var(--arcane-glow); transition: filter 0.15s;
}
.ad-btn-primary:hover:not(:disabled) { filter: brightness(1.1); }
.ad-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
.ad-body { display: grid; grid-template-columns: 1fr; flex: 1; min-height: 0; }
@media (min-width: 900px) { .ad-body { grid-template-columns: 1fr 340px; } }

/* Abas de navegação na topbar */
.ad-topnav {
  display: flex; align-items: center; gap: 4px;
  margin-left: 12px; padding-left: 16px;
  border-left: 1px solid var(--border-soft);
  height: 32px;
}
.ad-topnav-item {
  background: transparent; border: none;
  color: var(--text-muted); font-family: inherit;
  font-size: 13px; font-weight: 500;
  padding: 6px 12px; border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s;
}
.ad-topnav-item:hover { color: var(--text); background: rgba(99,102,241,0.08); }
.ad-topnav-item.active {
  color: var(--text);
  background: rgba(99,102,241,0.15);
  box-shadow: inset 0 0 0 1px rgba(99,102,241,0.3);
}
@media (max-width: 900px) {
  .ad-topnav { display: none; } /* esconde no mobile, dropdown ⋯ assume */
}
.ad-side {
  border-right: 1px solid var(--border-soft);
  background: rgba(2,6,23,0.4); padding: 16px;
  height: calc(100vh - 54px);
  position: sticky; top: 54px;
  overflow-y: auto;
}
.ad-side-right {
  border-right: none; border-left: 1px solid var(--border-soft);
  background: rgba(2,6,23,0.5);
}
.ad-side-head {
  display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px;
}
.ad-side-title {
  display: inline-flex; align-items: center; gap: 8px;
  border: 1px solid var(--border-soft); background: var(--panel);
  padding: 6px 10px; border-radius: 8px;
  font-size: 11px; font-weight: 500; color: var(--text);
}
.ad-nav { display: flex; flex-direction: column; gap: 4px; margin-bottom: 16px; }
.ad-nav-label {
  font-size: 9px; font-weight: 700; color: var(--text-dim);
  letter-spacing: 0.2em; text-transform: uppercase;
  padding: 4px 10px 2px;
}
.ad-nav-item {
  display: flex; align-items: center; gap: 10px;
  padding: 8px 10px; border-radius: 8px;
  border: 1px solid transparent; background: transparent;
  color: var(--text-muted); font-size: 12px; text-align: left;
  transition: all 0.15s; width: 100%;
}
.ad-nav-item:hover { background: rgba(30,41,59,0.5); color: var(--text); }
.ad-nav-item.active {
  background: var(--panel); border-color: var(--border); color: var(--text);
  box-shadow: inset 0 0 0 1px rgba(99,102,241,0.25);
}
.ad-nav-icon { width: 16px; text-align: center; font-size: 13px; opacity: 0.85; }
.ad-nav-badge {
  margin-left: auto; font-size: 10px;
  background: rgba(99,102,241,0.1); color: #a5b4fc;
  padding: 2px 8px; border-radius: 999px;
  border: 1px solid rgba(99,102,241,0.15);
}
.ad-nav-dot { margin-left: auto; width: 6px; height: 6px; border-radius: 50%; background: #f59e0b; }
.ad-nav-lock { margin-left: auto; font-size: 11px; opacity: 0.7; }
.ad-main { background: transparent; position: relative; min-height: calc(100vh - 54px); }
.ad-main-sub {
  display: flex; align-items: center; gap: 8px;
  padding: 12px 24px; border-bottom: 1px solid var(--border-soft);
  font-size: 11px; color: var(--text-muted);
  background: rgba(2,6,23,0.3);
}
.ad-main-sub-chip {
  display: inline-flex; align-items: center; justify-content: center;
  width: 20px; height: 20px; border-radius: 4px;
  background: var(--arcane); color: #fff; font-size: 11px;
}
.ad-main-sub b { color: var(--text); font-weight: 500; }
.ad-main-body { padding: 40px 48px 56px; max-width: 1400px; margin: 0 auto; width: 100%; }
@media (max-width: 700px) { .ad-main-body { padding: 20px; } }
.ad-hero {
  position: relative; overflow: hidden;
  border: 1px solid var(--border-soft); background: rgba(2,6,23,0.7);
  border-radius: 18px; padding: 36px 36px; margin-bottom: 28px;
}
.ad-hero::before {
  content: ''; position: absolute; right: -80px; top: -80px;
  width: 280px; height: 280px; border-radius: 50%;
  background: var(--arcane); opacity: 0.18; filter: blur(60px); pointer-events: none;
}
.ad-hero-label {
  display: inline-flex; align-items: center; gap: 6px;
  border: 1px solid var(--border); background: rgba(0,0,0,0.3);
  padding: 5px 12px; border-radius: 999px;
  font-size: 10px; letter-spacing: 0.18em; text-transform: uppercase;
  color: var(--text-muted); margin-bottom: 14px; position: relative; z-index: 1;
}
.ad-hero-label-dot { width: 6px; height: 6px; border-radius: 50%; background: var(--arcane); }
.ad-hero h2 {
  font-family: 'Space Grotesk', 'Inter', sans-serif;
  font-size: 32px; font-weight: 500; letter-spacing: -0.015em;
  margin: 0 0 8px 0; color: #f8fafc; position: relative; z-index: 1;
}
.ad-hero p { margin: 0; color: var(--text-muted); font-size: 14px; max-width: 600px; position: relative; z-index: 1; line-height: 1.6; }
.ad-stats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin-bottom: 28px; }
@media (max-width: 700px) { .ad-stats { grid-template-columns: 1fr; } }
.ad-stat {
  border: 1px solid var(--border-soft); background: var(--panel);
  border-radius: 12px; padding: 18px;
}
.ad-stat-label { font-size: 10px; letter-spacing: 0.15em; text-transform: uppercase; color: var(--text-dim); margin-bottom: 8px; font-weight: 600; }
.ad-stat-value { font-family: 'JetBrains Mono', 'Space Mono', monospace; font-size: 24px; font-weight: 700; font-variant-numeric: tabular-nums; }
.ad-stat-sub { font-size: 12px; color: var(--text-dim); margin-top: 4px; }
.ad-mods-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
@media (max-width: 1000px) { .ad-mods-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 640px) { .ad-mods-grid { grid-template-columns: 1fr; } }
.ad-modcard {
  text-align: left; border: 1px solid var(--border-soft);
  background: var(--panel); border-radius: 16px; padding: 28px;
  transition: all 0.2s; display: flex; flex-direction: column; gap: 14px;
  min-height: 180px;
}
.ad-modcard:hover:not(:disabled) {
  border-color: rgba(99,102,241,0.4);
  background: rgba(30,41,59,0.6); transform: translateY(-2px);
}
.ad-modcard:disabled { opacity: 0.5; cursor: not-allowed; }
.ad-modcard-head { display: flex; justify-content: space-between; align-items: flex-start; }
.ad-modcard-icon {
  width: 52px; height: 52px; border-radius: 12px;
  display: inline-flex; align-items: center; justify-content: center; font-size: 24px;
}
.ad-modcard h3 { font-size: 18px; font-weight: 600; margin: 0; color: var(--text); }
.ad-modcard p { font-size: 13px; color: var(--text-dim); line-height: 1.6; margin: 0; }
.ad-modcard-locked { font-size: 11px; color: #f59e0b; }

/* CARD ARCANE TESTE - destaque principal */
.ad-arcane-teste-card {
  position: relative; overflow: hidden;
  display: flex; align-items: center; gap: 24px;
  width: 100%; padding: 32px 36px; margin-bottom: 32px;
  background: linear-gradient(135deg, rgba(99,102,241,0.12) 0%, rgba(99,102,241,0.04) 100%);
  border: 1px solid rgba(99,102,241,0.35);
  border-radius: 18px; cursor: pointer;
  transition: all 0.25s ease;
  text-align: left; font-family: inherit;
  box-shadow: 0 0 0 1px rgba(99,102,241,0.08), 0 12px 40px rgba(99,102,241,0.08);
}
.ad-arcane-teste-card:hover {
  border-color: rgba(99,102,241,0.6);
  background: linear-gradient(135deg, rgba(99,102,241,0.18) 0%, rgba(99,102,241,0.06) 100%);
  transform: translateY(-2px);
  box-shadow: 0 0 0 1px rgba(99,102,241,0.15), 0 20px 60px rgba(99,102,241,0.18);
}
.ad-arcane-teste-glow {
  position: absolute; right: -100px; top: -120px;
  width: 380px; height: 380px; border-radius: 50%;
  background: var(--arcane); opacity: 0.18; filter: blur(80px);
  pointer-events: none;
}
.ad-arcane-teste-content {
  position: relative; z-index: 1; flex: 1;
  display: flex; flex-direction: column; gap: 8px;
}
.ad-arcane-teste-badge {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 4px 10px; border-radius: 999px;
  background: rgba(99,102,241,0.18);
  border: 1px solid rgba(99,102,241,0.35);
  font-size: 10px; font-weight: 600; letter-spacing: 0.12em; text-transform: uppercase;
  color: #c7d2fe; align-self: flex-start; margin-bottom: 4px;
}
.ad-arcane-teste-badge-dot {
  width: 6px; height: 6px; border-radius: 50%;
  background: var(--arcane);
  animation: pulse-dot 2s ease infinite;
}
@keyframes pulse-dot {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.6; transform: scale(1.3); }
}
.ad-arcane-teste-title {
  font-family: 'Space Grotesk', 'Inter', sans-serif;
  font-size: 28px; font-weight: 600; letter-spacing: -0.015em;
  margin: 0; color: #f8fafc;
}
.ad-arcane-teste-desc {
  font-size: 14px; line-height: 1.6;
  color: var(--text-muted); margin: 0;
  max-width: 600px;
}
.ad-arcane-teste-cta {
  display: inline-flex; align-items: center; gap: 8px;
  margin-top: 10px;
  font-size: 13px; font-weight: 600;
  color: #a5b4fc;
  transition: gap 0.2s;
}
.ad-arcane-teste-card:hover .ad-arcane-teste-cta { gap: 12px; }
.ad-arcane-teste-icon {
  position: relative; z-index: 1;
  width: 90px; height: 90px; border-radius: 20px;
  display: flex; align-items: center; justify-content: center;
  background: rgba(99,102,241,0.15);
  border: 1px solid rgba(99,102,241,0.35);
  color: var(--arcane);
  flex-shrink: 0;
}
@media (max-width: 700px) {
  .ad-arcane-teste-card { padding: 24px; gap: 16px; flex-direction: column; align-items: flex-start; }
  .ad-arcane-teste-title { font-size: 22px; }
  .ad-arcane-teste-desc { font-size: 13px; }
  .ad-arcane-teste-icon { width: 64px; height: 64px; align-self: flex-end; margin-top: -56px; }
}

/* Divisor "Outros módulos" */
.ad-section-divider {
  display: flex; align-items: center; gap: 14px;
  margin: 12px 0 20px 0;
  color: var(--text-dim);
  font-size: 11px; font-weight: 500; letter-spacing: 0.18em; text-transform: uppercase;
}
.ad-section-divider::before,
.ad-section-divider::after {
  content: ''; flex: 1; height: 1px;
  background: linear-gradient(to right, transparent, var(--border-soft), transparent);
}

/* ===== WIZARD ARCANE TESTE ===== */
.ad-wizard-progress {
  display: flex; align-items: center; gap: 8px;
  margin-bottom: 24px; padding: 16px 20px;
  background: rgba(2,6,23,0.5); border: 1px solid var(--border-soft);
  border-radius: 14px;
}
.ad-wizard-step {
  flex: 1; display: flex; align-items: center; gap: 10px;
  position: relative;
  opacity: 0.4; transition: opacity 0.3s;
}
.ad-wizard-step.done, .ad-wizard-step.current { opacity: 1; }
.ad-wizard-step:not(:last-child)::after {
  content: ''; position: absolute; right: -8px; top: 50%;
  width: 8px; height: 1px; background: var(--border);
}
.ad-wizard-step-num {
  width: 28px; height: 28px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  background: rgba(99,102,241,0.15); border: 1px solid rgba(99,102,241,0.4);
  color: var(--arcane); font-size: 12px; font-weight: 600;
  flex-shrink: 0;
}
.ad-wizard-step.done .ad-wizard-step-num {
  background: var(--arcane); border-color: var(--arcane); color: #fff;
}
.ad-wizard-step.current .ad-wizard-step-num {
  background: rgba(99,102,241,0.25); border-color: var(--arcane);
  box-shadow: 0 0 0 4px rgba(99,102,241,0.1);
}
.ad-wizard-step-label { font-size: 12px; color: var(--text-muted); font-weight: 500; }
.ad-wizard-step.current .ad-wizard-step-label { color: var(--text); }

.ad-wizard-card {
  background: var(--panel); border: 1px solid var(--border-soft);
  border-radius: 16px; padding: 28px;
  display: flex; flex-direction: column; gap: 24px;
  margin-bottom: 24px;
}
.ad-wizard-q { display: flex; flex-direction: column; gap: 10px; }
.ad-wizard-label {
  font-size: 13px; font-weight: 500; color: var(--text);
  letter-spacing: 0.01em;
}

.ad-chip-grid { display: flex; flex-wrap: wrap; gap: 8px; }
.ad-chip {
  padding: 9px 16px; border-radius: 10px;
  background: rgba(2,6,23,0.5); border: 1px solid var(--border-soft);
  color: var(--text-muted); font-family: inherit;
  font-size: 13px; font-weight: 500;
  cursor: pointer; transition: all 0.15s;
}
.ad-chip:hover { background: rgba(99,102,241,0.08); border-color: rgba(99,102,241,0.2); color: var(--text); }
.ad-chip.selected {
  background: rgba(99,102,241,0.2); border-color: var(--arcane);
  color: #c7d2fe; font-weight: 600;
}

.ad-area-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
@media (max-width: 700px) { .ad-area-grid { grid-template-columns: 1fr; } }
.ad-area-card {
  display: flex; align-items: flex-start; gap: 14px;
  padding: 18px; border-radius: 12px;
  background: rgba(2,6,23,0.5); border: 1px solid var(--border-soft);
  text-align: left; cursor: pointer; transition: all 0.15s;
  font-family: inherit;
}
.ad-area-card:hover { background: rgba(99,102,241,0.06); border-color: rgba(99,102,241,0.25); }
.ad-area-card.selected {
  background: rgba(99,102,241,0.12);
  border-color: var(--arcane);
  box-shadow: 0 0 0 1px rgba(99,102,241,0.3);
}
.ad-area-icon { font-size: 28px; line-height: 1; flex-shrink: 0; }
.ad-area-content { flex: 1; display: flex; flex-direction: column; gap: 4px; }
.ad-area-label { font-size: 14px; font-weight: 600; color: var(--text); }
.ad-area-dor { font-size: 12px; color: var(--text-dim); font-style: italic; line-height: 1.4; }
.ad-area-check {
  width: 22px; height: 22px; border-radius: 50%;
  border: 1.5px solid var(--border);
  display: flex; align-items: center; justify-content: center;
  color: #fff; font-size: 12px; font-weight: 600;
  flex-shrink: 0;
}
.ad-area-card.selected .ad-area-check {
  background: var(--arcane); border-color: var(--arcane);
}

.ad-wizard-textarea {
  width: 100%; min-height: 200px;
  padding: 16px; border-radius: 12px;
  background: rgba(2,6,23,0.5); border: 1px solid var(--border-soft);
  color: var(--text); font-family: inherit; font-size: 14px; line-height: 1.6;
  resize: vertical; transition: border-color 0.15s;
}
.ad-wizard-textarea:focus { outline: none; border-color: rgba(99,102,241,0.5); }
.ad-wizard-textarea::placeholder { color: var(--text-dim); font-style: italic; }

.ad-wizard-quality { display: flex; flex-direction: column; gap: 8px; }
.ad-wizard-quality-bar {
  height: 6px; border-radius: 3px;
  background: rgba(2,6,23,0.6); overflow: hidden;
}
.ad-wizard-quality-fill { height: 100%; transition: width 0.3s, background 0.3s; border-radius: 3px; }
.ad-wizard-quality-info {
  display: flex; align-items: center; justify-content: space-between;
  font-size: 12px;
}
.ad-wizard-quality-tip { color: var(--text-muted); }

.ad-wizard-use-example {
  background: transparent; border: 1px dashed var(--border);
  color: var(--text-muted); font-family: inherit; font-size: 12px;
  padding: 10px 14px; border-radius: 10px; cursor: pointer;
  transition: all 0.15s;
}
.ad-wizard-use-example:hover { border-color: rgba(99,102,241,0.4); color: var(--text); }

.ad-objective {
  display: flex; align-items: center; gap: 12px;
  padding: 14px 18px; border-radius: 12px;
  background: rgba(2,6,23,0.5); border: 1px solid var(--border-soft);
  color: var(--text); font-family: inherit; font-size: 13px;
  text-align: left; cursor: pointer; transition: all 0.15s;
}
.ad-objective:hover { background: rgba(99,102,241,0.06); border-color: rgba(99,102,241,0.25); }
.ad-objective.selected {
  background: rgba(99,102,241,0.12); border-color: var(--arcane);
}
.ad-objective-check {
  width: 20px; height: 20px; border-radius: 5px;
  border: 1.5px solid var(--border);
  display: flex; align-items: center; justify-content: center;
  font-size: 11px; font-weight: 600; color: #fff;
  flex-shrink: 0;
}
.ad-objective.selected .ad-objective-check {
  background: var(--arcane); border-color: var(--arcane);
}

.ad-wizard-actions {
  display: flex; align-items: center; justify-content: space-between;
  margin-top: 8px; padding-top: 20px;
  border-top: 1px solid var(--border-soft);
}
.ad-wizard-back {
  background: transparent; border: none;
  color: var(--text-muted); font-family: inherit; font-size: 13px;
  cursor: pointer; padding: 8px 4px;
}
.ad-wizard-back:hover { color: var(--text); }
.ad-wizard-next, .ad-wizard-submit {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 11px 20px; border-radius: 10px;
  background: var(--arcane); border: none;
  color: #fff; font-family: inherit; font-size: 13px; font-weight: 600;
  cursor: pointer; transition: all 0.15s;
}
.ad-wizard-next:hover:not(:disabled), .ad-wizard-submit:hover:not(:disabled) {
  background: #5558e3; transform: translateY(-1px);
  box-shadow: 0 8px 24px rgba(99,102,241,0.3);
}
.ad-wizard-next:disabled, .ad-wizard-submit:disabled {
  opacity: 0.4; cursor: not-allowed;
}
.ad-wizard-submit {
  background: linear-gradient(135deg, #6366F1, #8b5cf6);
  padding: 13px 26px; font-size: 14px;
}

/* LOADING */
.ad-wizard-loading {
  display: flex; flex-direction: column; align-items: center; gap: 18px;
  padding: 60px 30px; text-align: center;
  background: var(--panel); border: 1px solid var(--border-soft);
  border-radius: 16px;
}
.ad-wizard-loading-icon {
  color: var(--arcane);
  animation: spin 2s linear infinite;
}
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
.ad-wizard-loading h3 {
  font-family: 'Space Grotesk', 'Inter', sans-serif;
  font-size: 22px; font-weight: 500; margin: 0; color: var(--text);
}
.ad-wizard-loading p { color: var(--text-muted); font-size: 13px; max-width: 400px; margin: 0; }
.ad-wizard-loading-steps {
  display: flex; flex-direction: column; gap: 8px;
  margin-top: 16px; text-align: left;
  font-size: 12px; color: var(--text-dim);
}
.ad-wizard-loading-steps .done { color: #34d399; }
.ad-wizard-loading-steps .loading { color: var(--arcane); animation: pulse-text 1.5s ease infinite; }
@keyframes pulse-text { 0%, 100% { opacity: 0.6; } 50% { opacity: 1; } }

/* RESULTADO */
.ad-result-preview {
  background: var(--panel); border: 1px solid var(--border-soft);
  border-radius: 14px; padding: 24px 28px;
  font-size: 14px; line-height: 1.7; color: var(--text);
  margin-bottom: 16px;
}
.ad-paywall {
  position: relative;
  background: linear-gradient(180deg, transparent 0%, rgba(99,102,241,0.05) 50%, rgba(99,102,241,0.12) 100%);
  border: 1px solid rgba(99,102,241,0.3);
  border-radius: 16px; padding: 36px 28px;
  text-align: center; overflow: hidden;
}
.ad-paywall-blur {
  position: absolute; left: 0; right: 0; top: -40px; height: 60px;
  background: linear-gradient(180deg, var(--panel) 0%, transparent 100%);
  filter: blur(8px); pointer-events: none;
}
.ad-paywall-content { position: relative; z-index: 1; display: flex; flex-direction: column; align-items: center; gap: 12px; }
.ad-paywall-icon { font-size: 32px; }
.ad-paywall-content h3 {
  font-family: 'Space Grotesk', 'Inter', sans-serif;
  font-size: 22px; font-weight: 500; margin: 0; color: var(--text);
}
.ad-paywall-content p { color: var(--text-muted); font-size: 13px; max-width: 480px; margin: 0; line-height: 1.6; }
.ad-paywall-cta {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 14px 28px; border-radius: 12px;
  background: linear-gradient(135deg, #6366F1, #8b5cf6); border: none;
  color: #fff; font-family: inherit; font-size: 14px; font-weight: 600;
  cursor: pointer; margin-top: 8px;
  box-shadow: 0 8px 24px rgba(99,102,241,0.4);
  transition: all 0.15s;
}
.ad-paywall-cta:hover { transform: translateY(-1px); box-shadow: 0 12px 32px rgba(99,102,241,0.5); }
.ad-paywall-restart {
  background: transparent; border: none;
  color: var(--text-dim); font-family: inherit; font-size: 12px;
  cursor: pointer; padding: 8px 12px; margin-top: 4px;
}
.ad-paywall-restart:hover { color: var(--text-muted); }
.ad-plan-tag {
  display: inline-flex; align-items: center; padding: 3px 8px; border-radius: 6px;
  font-size: 9px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase;
}
.ad-plan-tag.free { background: rgba(148,163,184,0.1); color: #94a3b8; }
.ad-plan-tag.essencial { background: rgba(56,189,248,0.1); color: #38bdf8; }
.ad-plan-tag.profissional { background: rgba(99,102,241,0.12); color: #a5b4fc; }
.ad-plan-tag.gestao { background: rgba(168,85,247,0.12); color: #c084fc; }
.ad-tool-row {
  display: flex; align-items: center; gap: 14px; width: 100%; text-align: left;
  border: 1px solid var(--border-soft); background: var(--panel);
  border-radius: 12px; padding: 14px 18px; margin-bottom: 8px; transition: all 0.15s;
  color: var(--text);
  font-family: inherit;
}
.ad-tool-row:hover:not(:disabled) {
  border-color: rgba(99,102,241,0.35); background: rgba(30,41,59,0.7);
}
.ad-tool-row:disabled { opacity: 0.45; cursor: not-allowed; }
.ad-tool-row .name { font-size: 14px; font-weight: 500; color: var(--text); margin-bottom: 3px; }
.ad-tool-row .desc { font-size: 12px; color: var(--text-dim); }
.ad-tool-row .side { display: flex; gap: 10px; align-items: center; flex-shrink: 0; }
.ad-tool-row .saved { font-size: 11px; color: #34d399; }
.ad-tool-row .upload-tag {
  font-size: 10px; background: rgba(16,185,129,0.08); color: #34d399;
  padding: 3px 8px; border-radius: 6px; font-weight: 600; border: 1px solid rgba(16,185,129,0.15);
}
.ad-tool-row .chev { font-size: 12px; color: var(--text-dim); }
.ad-toolform { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
@media (max-width: 900px) { .ad-toolform { grid-template-columns: 1fr; } }
.ad-field-label {
  font-size: 10px; color: var(--text-dim);
  letter-spacing: 0.15em; text-transform: uppercase; font-weight: 600;
  margin-bottom: 8px; display: block;
}
.ad-textarea, .ad-input {
  width: 100%; background: rgba(2,6,23,0.6);
  border: 1px solid var(--border-soft); border-radius: 10px;
  padding: 12px 14px; color: var(--text); font-size: 13px;
  outline: none; transition: border-color 0.15s;
}
.ad-textarea:focus, .ad-input:focus {
  border-color: rgba(99,102,241,0.5); box-shadow: 0 0 0 3px rgba(99,102,241,0.1);
}
.ad-textarea { resize: vertical; min-height: 120px; }
.ad-upload {
  border: 2px dashed var(--border); background: rgba(2,6,23,0.5);
  border-radius: 12px; padding: 24px; text-align: center; cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
}
.ad-upload:hover, .ad-upload.drag {
  border-color: rgba(99,102,241,0.5); background: rgba(99,102,241,0.04);
}
.ad-upload-file { font-size: 14px; color: #34d399; font-weight: 500; margin-bottom: 4px; }
.ad-upload-sub { font-size: 12px; color: var(--text-dim); }
.ad-upload-remove {
  margin-top: 8px; background: transparent; color: #f43f5e;
  font-size: 11px; padding: 0; border: none;
}
.ad-execute {
  width: 100%; height: 48px; margin-top: 14px;
  background: var(--arcane); color: #fff; border: none; border-radius: 12px;
  font-size: 14px; font-weight: 600;
  box-shadow: 0 0 24px var(--arcane-glow); transition: filter 0.15s;
}
.ad-execute:hover:not(:disabled) { filter: brightness(1.1); }
.ad-execute:disabled { opacity: 0.5; cursor: not-allowed; }
.ad-result {
  background: rgba(2,6,23,0.6); border: 1px solid var(--border-soft);
  border-radius: 12px; padding: 16px;
  font-size: 13px; line-height: 1.7; color: var(--text-muted);
  white-space: pre-wrap; min-height: 240px; max-height: 520px; overflow-y: auto;
}
.ad-panel {
  border: 1px solid var(--border-soft); background: var(--panel);
  border-radius: 12px; padding: 16px; margin-bottom: 12px;
}
.ad-panel-head {
  display: flex; justify-content: space-between; align-items: center;
  font-size: 12px; color: var(--text); margin-bottom: 12px; font-weight: 500;
}
.ad-panel-badge {
  font-size: 10px; background: rgba(2,6,23,0.8); color: var(--text-dim);
  padding: 3px 8px; border-radius: 5px;
}
.ad-avatar {
  width: 44px; height: 44px; border-radius: 50%;
  background: var(--arcane); color: #fff;
  display: inline-flex; align-items: center; justify-content: center;
  font-weight: 600; flex-shrink: 0; font-size: 16px;
}
.ad-btn-ghost {
  width: 100%; border: 1px solid var(--border-soft); background: rgba(2,6,23,0.5);
  color: var(--text); padding: 10px 14px; border-radius: 9px;
  font-size: 12px; font-weight: 500; margin-top: 12px; transition: background 0.15s;
}
.ad-btn-ghost:hover { background: rgba(30,41,59,0.7); }
.ad-btn-ghost-small { padding: 8px 12px; font-size: 11px; margin-top: 10px; }
.ad-usage-bar {
  position: relative; height: 6px; background: rgba(30,41,59,0.8);
  border-radius: 3px; overflow: hidden;
}
.ad-usage-fill {
  position: absolute; inset: 0 auto 0 0;
  background: var(--arcane); border-radius: 3px;
  box-shadow: 0 0 8px var(--arcane-glow); transition: width 0.5s;
}
.ad-hist-item {
  padding: 8px 10px; border-radius: 8px; cursor: pointer;
  font-size: 11px; color: var(--text-muted); transition: background 0.15s;
}
.ad-hist-item:hover { background: rgba(30,41,59,0.6); color: var(--text); }
.ad-hist-item .top { display: flex; gap: 6px; align-items: center; }
.ad-hist-item .dot { width: 5px; height: 5px; border-radius: 50%; background: var(--arcane); flex-shrink: 0; }
.ad-hist-item .name { font-size: 11px; font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: var(--text); }
.ad-hist-item .meta { display: flex; justify-content: space-between; font-size: 10px; color: var(--text-dim); padding-left: 11px; margin-top: 2px; }
.ad-crumb { display: flex; align-items: center; gap: 10px; margin-bottom: 18px; font-size: 12px; color: var(--text-dim); }
.ad-crumb button { background: transparent; border: none; color: var(--text-dim); font-size: 12px; padding: 0; }
.ad-crumb button:hover { color: var(--text); }
.ad-crumb-sep { color: var(--border); }
.ad-crumb-current { text-transform: uppercase; letter-spacing: 0.1em; color: var(--text-muted); font-size: 11px; }
.ad-toast {
  position: fixed; bottom: 24px; left: 50%; transform: translateX(-50%);
  background: rgba(15,23,42,0.95); backdrop-filter: blur(12px);
  border: 1px solid var(--border);
  padding: 12px 20px; border-radius: 12px;
  color: var(--text); font-size: 13px;
  box-shadow: 0 20px 40px rgba(0,0,0,0.5); z-index: 100;
}
.ad-plans-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; }
@media (max-width: 1100px) { .ad-plans-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 600px) { .ad-plans-grid { grid-template-columns: 1fr; } }
.ad-plancard {
  position: relative; border: 1px solid var(--border-soft);
  background: var(--panel); border-radius: 16px; padding: 22px; transition: all 0.3s;
}
.ad-plancard.current { border-color: rgba(99,102,241,0.4); }
.ad-plancard.highlight { border-color: rgba(99,102,241,0.3); box-shadow: 0 0 32px rgba(99,102,241,0.08); }
.ad-plancard:hover:not(.current) { transform: translateY(-3px); border-color: rgba(99,102,241,0.35); }
.ad-plancard-tag {
  position: absolute; top: -10px; left: 50%; transform: translateX(-50%);
  background: var(--arcane); color: #fff;
  font-size: 9px; font-weight: 700; letter-spacing: 0.15em;
  padding: 4px 14px; border-radius: 6px; text-transform: uppercase; white-space: nowrap;
}
.ad-plancard-current {
  position: absolute; top: 10px; right: 14px;
  font-size: 10px; color: #a5b4fc; font-weight: 600; letter-spacing: 0.1em;
}
.ad-plancard-name { font-size: 11px; color: var(--text-muted); font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase; margin-bottom: 10px; }
.ad-plancard-price { font-family: 'JetBrains Mono', monospace; font-size: 26px; font-weight: 700; color: var(--text); }
.ad-plancard-price .period { font-size: 12px; color: var(--text-dim); font-weight: 400; }
.ad-plancard-feats { display: flex; flex-direction: column; gap: 8px; margin: 14px 0 18px; padding: 14px 0 0; border-top: 1px solid var(--border-soft); }
.ad-plancard-feat { font-size: 12px; color: var(--text-muted); display: flex; gap: 8px; }
.ad-plancard-feat .check { color: #10b981; flex-shrink: 0; }
.ad-plancard-btn {
  width: 100%; height: 38px; border-radius: 10px;
  font-size: 12px; font-weight: 600;
  background: var(--arcane); color: #fff; border: none; transition: filter 0.15s;
}
.ad-plancard-btn:hover:not(:disabled) { filter: brightness(1.1); }
.ad-plancard-btn:disabled { opacity: 0.5; cursor: default; }
.ad-plancard-btn.ghost { background: transparent; border: 1px solid var(--border); color: var(--text); }
.ad-plancard-btn.ghost:hover:not(:disabled) { background: rgba(30,41,59,0.7); filter: none; }
.ad-online {
  display: flex; align-items: center; gap: 8px; padding: 4px 10px;
  border: 1px solid rgba(16,185,129,0.15); background: rgba(16,185,129,0.04);
  border-radius: 8px; font-size: 10px; color: #34d399;
  letter-spacing: 0.1em; text-transform: uppercase; font-weight: 600;
}
.ad-online-pulse {
  width: 6px; height: 6px; border-radius: 50%;
  background: #10b981; position: relative;
}
.ad-online-pulse::after {
  content: ''; position: absolute; inset: 0; border-radius: 50%;
  background: #34d399; opacity: 0.6;
  animation: ad-ping 2s infinite;
}
@keyframes ad-ping {
  0% { transform: scale(1); opacity: 0.6; }
  80%, 100% { transform: scale(2.5); opacity: 0; }
}
.ad-hist-back-btn {
  display: none;
  align-items: center; gap: 8px;
  background: transparent; border: 1px solid var(--border-soft);
  color: var(--text); font-family: inherit;
  padding: 8px 12px; border-radius: 8px;
  font-size: 13px; font-weight: 500;
  cursor: pointer; margin-bottom: 14px;
}
.ad-hist-back-btn:hover { background: rgba(99,102,241,0.1); }

.ad-mobile-btn { display: none; }
.ad-mobile-menu-btn {
  display: none;
  align-items: center; justify-content: center;
  width: 36px; height: 36px;
  background: transparent; border: 1px solid var(--border-soft);
  color: var(--text); border-radius: 9px;
  cursor: pointer; padding: 0;
  font-family: inherit;
}
.ad-mobile-menu-btn:hover { background: rgba(99,102,241,0.1); }
.ad-mobile-menu-backdrop {
  position: fixed; inset: 0; z-index: 9998;
  background: transparent;
  width: 100vw; height: 100vh;
  overflow: hidden;
}
.ad-mobile-menu {
  position: fixed; top: 56px; right: 12px;
  z-index: 9999;
  background: #1a1f33;
  border: 1px solid var(--border);
  border-radius: 12px;
  box-shadow: 0 12px 40px rgba(0,0,0,0.6), 0 0 0 1px rgba(99,102,241,0.1);
  padding: 6px;
  min-width: 200px;
  max-width: calc(100vw - 24px);
  display: flex; flex-direction: column; gap: 2px;
}
.ad-mobile-menu-item {
  display: flex; align-items: center; gap: 10px;
  background: transparent; border: none;
  color: var(--text); font-size: 13px; font-weight: 500;
  padding: 10px 12px; border-radius: 8px;
  cursor: pointer; text-align: left;
  font-family: inherit;
  transition: background 0.15s;
}
.ad-mobile-menu-item:hover { background: rgba(99,102,241,0.15); }
.ad-mobile-menu-item svg { opacity: 0.7; flex-shrink: 0; }

@media (max-width: 900px) {
  /* Mostra botão de menu mobile */
  .ad-mobile-menu-btn { display: inline-flex; }

  /* Layout: 1 coluna */
  .ad-body { grid-template-columns: 1fr !important; }

  /* SIDEBAR ESQUERDA: esconde no mobile (usuario navega pelo dropdown) */
  .ad-side:not(.ad-side-right) { display: none !important; }

  /* SIDEBAR DIREITA: vai pro FIM da pagina como card */
  .ad-side-right {
    position: relative; top: auto;
    height: auto; max-height: none;
    width: 100%; padding: 12px 14px;
    border-left: none;
    border-top: 1px solid var(--border-soft);
    overflow-y: visible;
    order: 99;
  }
  .ad-side-right .ad-panel { margin-bottom: 8px; padding: 12px; }

  /* MAIN: ocupa toda largura */
  .ad-main-body { padding: 16px !important; max-width: 100% !important; }
  .ad-main-sub { padding: 10px 14px; font-size: 11px; }

  /* TOPBAR: compacta */
  .ad-topbar { padding: 10px 12px; height: auto; min-height: 50px; }
  .ad-topbar-actions { gap: 6px; }
  .ad-online { display: none; }
  .ad-chip-muted { padding: 4px 10px; font-size: 10px; }
  .ad-btn-primary { padding: 6px 10px; font-size: 11px; }

  /* BRAND: simplifica */
  .ad-brand-pill { font-size: 12px; gap: 6px; }
  .ad-brand-pill > span:nth-child(3),
  .ad-brand-pill > span:nth-child(4) { display: none; }

  /* HERO compacto */
  .ad-hero { padding: 24px 16px !important; }
  .ad-hero-title { font-size: 22px !important; line-height: 1.2 !important; }
  .ad-hero-sub { font-size: 13px !important; }

  /* HISTORICO em mobile: tela cheia (lista OU detalhe) */
  .ad-hist-grid { grid-template-columns: 1fr !important; }
  .ad-hist-grid.has-detail .ad-hist-list { display: none; }
  .ad-hist-detail {
    position: relative !important;
    top: 0 !important;
    max-height: none !important;
    overflow: visible !important;
  }
  .ad-hist-back-btn { display: inline-flex; }
}

/* ==== MODAL (CompanyProfileModal) ==== */
.ad-modal-overlay {
  position: fixed; inset: 0; z-index: 1000;
  background: rgba(0,0,0,0.75); backdrop-filter: blur(8px);
  display: flex; align-items: center; justify-content: center;
  padding: 20px;
}
.ad-modal-box {
  width: 100%; max-width: 540px;
  border: 1px solid var(--border);
  background: linear-gradient(to bottom, rgba(15,23,42,0.95), rgba(2,6,23,0.95));
  border-radius: 20px;
  padding: 32px;
  box-shadow: 0 30px 120px rgba(0,0,0,0.8);
  color: var(--text);
}
.ad-modal-title {
  font-family: 'Space Grotesk', 'Inter', sans-serif;
  font-size: 22px; font-weight: 500; margin: 4px 0 10px;
}
.ad-modal-desc { font-size: 13px; color: var(--text-dim); line-height: 1.6; margin-bottom: 24px; }
.ad-modal-grid { display: flex; flex-direction: column; gap: 14px; }
.ad-modal-row2 { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
@media (max-width: 500px) { .ad-modal-row2 { grid-template-columns: 1fr; } }

.ad-select {
  width: 100%; background: rgba(2,6,23,0.6);
  border: 1px solid var(--border-soft); border-radius: 10px;
  padding: 10px 12px; color: var(--text); font-size: 13px;
  outline: none; transition: border-color 0.15s;
}
.ad-select:focus {
  border-color: rgba(99,102,241,0.5); box-shadow: 0 0 0 3px rgba(99,102,241,0.1);
}
.ad-input-basic {
  width: 100%; background: rgba(2,6,23,0.6);
  border: 1px solid var(--border-soft); border-radius: 10px;
  padding: 10px 12px; color: var(--text); font-size: 13px;
  outline: none; transition: border-color 0.15s;
}
.ad-input-basic:focus {
  border-color: rgba(99,102,241,0.5); box-shadow: 0 0 0 3px rgba(99,102,241,0.1);
}

.ad-modal-skip {
  background: transparent; border: none; color: var(--text-dim);
  font-size: 12px; padding: 8px 0; width: 100%; text-align: center;
}
.ad-modal-skip:hover { color: var(--text); }

.ad-section-label {
  display: inline-block; font-size: 10px; font-weight: 700;
  color: var(--text-dim); letter-spacing: 0.25em;
  text-transform: uppercase; margin-bottom: 8px;
}

/* ProfileEditor */
.ad-profile-wrap { max-width: 640px; }
`;

// ══════════════ SUBCOMPONENTES ══════════════
function Toast({ msg, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 3500); return () => clearTimeout(t); }, [onClose]);
  return <div className="ad-toast">{msg}</div>;
}
function FieldLabel({ children }) { return <label className="ad-field-label">{children}</label>; }
function PlanTag({ plan }) { return <span className={`ad-plan-tag ${plan}`}>{plan}</span>; }

function CompanyProfileModal({ userId, onComplete }) {
  const [form, setForm] = useState({ company_name: '', sector: '', company_size: '', tone: 'Neutro e profissional', city: '' });
  const [loading, setLoading] = useState(false);

  const save = async () => {
    if (!form.company_name.trim()) return;
    setLoading(true);
    await supabase.from('profiles').update({ ...form, profile_complete: true }).eq('id', userId);
    setLoading(false);
    onComplete(form);
  };

  return (
    <div className="ad-modal-overlay">
      <div className="ad-modal-box">
        <span className="ad-section-label">Bem-vindo ao Arcane</span>
        <div className="ad-modal-title">Conte-nos sobre sua empresa</div>
        <p className="ad-modal-desc">
          Essas informações personalizam todas as análises para o contexto do seu negócio. Você só preenche uma vez.
        </p>
        <div className="ad-modal-grid">
          <div>
            <FieldLabel>Nome da empresa *</FieldLabel>
            <input
              className="ad-input-basic"
              placeholder="Ex: Studio Criativo Ltda"
              value={form.company_name}
              onChange={(e) => setForm({ ...form, company_name: e.target.value })}
            />
          </div>
          <div className="ad-modal-row2">
            <div>
              <FieldLabel>Setor</FieldLabel>
              <select className="ad-select" value={form.sector} onChange={(e) => setForm({ ...form, sector: e.target.value })}>
                <option value="">Selecione...</option>
                {SECTORS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <FieldLabel>Tamanho</FieldLabel>
              <select className="ad-select" value={form.company_size} onChange={(e) => setForm({ ...form, company_size: e.target.value })}>
                <option value="">Selecione...</option>
                {SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div className="ad-modal-row2">
            <div>
              <FieldLabel>Cidade</FieldLabel>
              <input
                className="ad-input-basic"
                placeholder="Ex: São Paulo, SP"
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
              />
            </div>
            <div>
              <FieldLabel>Tom de comunicação</FieldLabel>
              <select className="ad-select" value={form.tone} onChange={(e) => setForm({ ...form, tone: e.target.value })}>
                {TONES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <button
            className="ad-execute"
            style={{ marginTop: 8 }}
            onClick={save}
            disabled={loading || !form.company_name.trim()}
          >
            {loading ? 'Salvando...' : 'Começar a usar o Arcane →'}
          </button>
          <button className="ad-modal-skip" onClick={() => onComplete(null)}>Pular por agora</button>
        </div>
      </div>
    </div>
  );
}

function ProfileEditor({ userId, profile, onSave, setToast }) {
  const [form, setForm] = useState({
    company_name: profile.company_name || '',
    sector: profile.sector || '',
    company_size: profile.company_size || '',
    tone: profile.tone || 'Neutro e profissional',
    city: profile.city || '',
  });
  const [loading, setLoading] = useState(false);

  const save = async () => {
    setLoading(true);
    await supabase.from('profiles').update({ ...form, profile_complete: true }).eq('id', userId);
    onSave(form);
    setToast('Perfil da empresa salvo!');
    setLoading(false);
  };

  return (
    <div className="ad-profile-wrap">
      <div className="ad-hero">
        <span className="ad-hero-label"><span className="ad-hero-label-dot" />Minha Empresa</span>
        <h2>Perfil da empresa</h2>
        <p>Esses dados personalizam todas as análises automaticamente. Você não precisa repetir o contexto em cada uso.</p>
      </div>
      <div className="ad-modal-grid">
        <div>
          <FieldLabel>Nome da empresa</FieldLabel>
          <input
            className="ad-input-basic"
            placeholder="Ex: Studio Criativo Ltda"
            value={form.company_name}
            onChange={(e) => setForm({ ...form, company_name: e.target.value })}
          />
        </div>
        <div className="ad-modal-row2">
          <div>
            <FieldLabel>Setor</FieldLabel>
            <select className="ad-select" value={form.sector} onChange={(e) => setForm({ ...form, sector: e.target.value })}>
              <option value="">Selecione...</option>
              {SECTORS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <FieldLabel>Tamanho</FieldLabel>
            <select className="ad-select" value={form.company_size} onChange={(e) => setForm({ ...form, company_size: e.target.value })}>
              <option value="">Selecione...</option>
              {SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>
        <div className="ad-modal-row2">
          <div>
            <FieldLabel>Cidade</FieldLabel>
            <input
              className="ad-input-basic"
              placeholder="Ex: São Paulo, SP"
              value={form.city}
              onChange={(e) => setForm({ ...form, city: e.target.value })}
            />
          </div>
          <div>
            <FieldLabel>Tom de comunicação</FieldLabel>
            <select className="ad-select" value={form.tone} onChange={(e) => setForm({ ...form, tone: e.target.value })}>
              {TONES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>
        <button className="ad-execute" style={{ marginTop: 8 }} onClick={save} disabled={loading}>
          {loading ? 'Salvando...' : 'Salvar perfil da empresa'}
        </button>
      </div>
    </div>
  );
}

// ══════════════ EMPRESA — HUB ══════════════
function EmpresaHub({ user, profile, metrics, goals, dres, history, saved, isPaidUser, isProfissionalPlus, onOpenMetricForm, onOpenGoalForm, onOpenDreUpload, onViewGoalPlan, onViewDre, onGenerateGoalPlan, onUpdateGoalStatus, onDeleteGoal, generatingPlan, onSaveProfile, setToast, navTo }) {
  const [showProfileEditor, setShowProfileEditor] = useState(false);

  // ─── Cálculos ───
  const totalAnalises = history.length;
  // calcSaved retorna { hours, value }. Defensivo: pode ser objeto, número, ou undefined
  const horasEconomizadas = (typeof saved === 'object' && saved !== null)
    ? (Number(saved.hours) || 0)
    : (Number(saved) || 0);
  const modulosUsados = new Set(history.map(h => h.module).filter(Boolean)).size;

  const ultimasAnalises = history.slice(0, 5);

  // ─── Métricas: pega última e penúltima pra mostrar variação ───
  const ultimaMetrica = metrics[0];   // mais recente (order desc)
  const penultimaMetrica = metrics[1];

  const calcDelta = (atual, anterior) => {
    const a = Number(atual);
    const b = Number(anterior);
    if (isNaN(a) || isNaN(b) || b === 0) return null;
    return ((a - b) / b) * 100;
  };

  const formatBRL = (n) => {
    const num = Number(n);
    if (isNaN(num)) return 'R$ 0';
    return 'R$ ' + num.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  };

  const formatMes = (dateStr) => {
    if (!dateStr) return '';
    const [y, m] = dateStr.split('-');
    const meses = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
    return `${meses[parseInt(m)-1]}/${y}`;
  };

  const goalsAtivas = goals.filter(g => g.status === 'ativa');

  // Calcula progresso de uma meta baseado em métricas
  const goalProgress = (goal) => {
    if (!goal.valor_alvo || !ultimaMetrica) return 0;
    let atual = 0;
    if (goal.tipo === 'faturamento') atual = ultimaMetrica.faturamento || 0;
    else if (goal.tipo === 'clientes') atual = ultimaMetrica.num_clientes || 0;
    else if (goal.tipo === 'vendas') atual = ultimaMetrica.num_vendas || 0;
    return Math.min(100, Math.round((atual / goal.valor_alvo) * 100));
  };

  if (showProfileEditor) {
    return (
      <div>
        <div className="ad-crumb">
          <button onClick={() => setShowProfileEditor(false)}>← Minha Empresa</button>
          <span className="ad-crumb-sep">|</span>
          <span className="ad-crumb-current">Editar dados</span>
        </div>
        <ProfileEditor
          userId={user.id}
          profile={profile}
          onSave={(d) => { onSaveProfile(d); setShowProfileEditor(false); }}
          setToast={setToast}
        />
      </div>
    );
  }

  return (
    <div className="ad-empresa">
      {/* HERO */}
      <div className="ad-hero">
        <span className="ad-hero-label"><span className="ad-hero-label-dot" />Minha Empresa</span>
        <h2>{profile.company_name || 'Configure sua empresa'}</h2>
        <p>
          {profile.sector && <>Setor: <b style={{ color: '#a5b4fc' }}>{profile.sector}</b>. </>}
          {profile.company_size && <>{profile.company_size}. </>}
          {profile.city && <>{profile.city}. </>}
          {!profile.company_name && 'Preencha os dados pra que o Arcane gere análises mais personalizadas.'}
        </p>
        <button
          className="ad-wizard-back"
          style={{ marginTop: 8, alignSelf: 'flex-start' }}
          onClick={() => setShowProfileEditor(true)}
        >Editar dados da empresa</button>
      </div>

      {/* BLOCO 2 — Uso do Arcane */}
      <div style={{ marginTop: 32 }}>
        <div className="ad-section-divider"><span>Seu uso do Arcane</span></div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginTop: 16 }}>
          <StatCard label="Análises geradas" value={totalAnalises} />
          <StatCard label="Horas economizadas" value={`~${horasEconomizadas.toFixed(1)}h`} />
          <StatCard label="Módulos diferentes" value={modulosUsados} />
          <StatCard label="Plano atual" value={profile.plan === 'free' || !profile.plan ? 'Gratuito' : (profile.plan === 'gestao' ? 'Enterprise' : profile.plan.charAt(0).toUpperCase() + profile.plan.slice(1))} />
        </div>

        {ultimasAnalises.length > 0 && (
          <div style={{ marginTop: 24 }}>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12 }}>
              Últimas análises
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {ultimasAnalises.map((h) => (
                <div key={h.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 16px', background: 'rgba(255,255,255,0.02)', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13 }}>
                  <span style={{ color: 'var(--text)' }}>{h.tool || h.module || 'Análise'}</span>
                  <span style={{ color: 'var(--text-muted)' }}>{new Date(h.created_at).toLocaleDateString('pt-BR')}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* BLOCO 3 — Saúde do negócio */}
      <div style={{ marginTop: 40 }}>
        <div className="ad-section-divider"><span>Saúde do negócio</span></div>

        {!ultimaMetrica && (
          <div style={{ marginTop: 16, padding: 24, background: 'rgba(255,255,255,0.02)', border: '1px dashed var(--border)', borderRadius: 12, textAlign: 'center' }}>
            <p style={{ color: 'var(--text-muted)', margin: '0 0 16px' }}>
              Você ainda não lançou nenhuma métrica. Comece registrando seus números desse mês.
            </p>
            <button className="ad-wizard-submit" onClick={onOpenMetricForm}>+ Lançar primeira métrica</button>
          </div>
        )}

        {ultimaMetrica && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginTop: 16 }}>
              <MetricCard
                label={`Faturamento ${formatMes(ultimaMetrica.mes)}`}
                value={formatBRL(ultimaMetrica.faturamento)}
                delta={penultimaMetrica ? calcDelta(ultimaMetrica.faturamento, penultimaMetrica.faturamento) : null}
              />
              <MetricCard
                label="Clientes ativos"
                value={ultimaMetrica.num_clientes || 0}
                delta={penultimaMetrica ? calcDelta(ultimaMetrica.num_clientes, penultimaMetrica.num_clientes) : null}
              />
              <MetricCard
                label="Vendas no mês"
                value={ultimaMetrica.num_vendas || 0}
                delta={penultimaMetrica ? calcDelta(ultimaMetrica.num_vendas, penultimaMetrica.num_vendas) : null}
              />
              <MetricCard
                label="Ticket médio"
                value={formatBRL(ultimaMetrica.ticket_medio)}
                delta={penultimaMetrica ? calcDelta(ultimaMetrica.ticket_medio, penultimaMetrica.ticket_medio) : null}
              />
            </div>

            {/* Gráfico simples de evolução do faturamento */}
            {metrics.length >= 2 && <FaturamentoChart metrics={metrics.slice(0, 6).reverse()} formatMes={formatMes} formatBRL={formatBRL} />}

            <div style={{ marginTop: 16, textAlign: 'center' }}>
              <button className="ad-wizard-back" onClick={onOpenMetricForm}>+ Lançar / atualizar mês</button>
            </div>
          </>
        )}
      </div>

      {/* BLOCO DRE — Profissional+ */}
      <div style={{ marginTop: 40 }}>
        <div className="ad-section-divider"><span>DRE Mensal</span></div>

        {!isProfissionalPlus && (
          <div style={{ marginTop: 16, padding: 24, background: 'rgba(167,139,250,0.04)', border: '1px solid rgba(167,139,250,0.2)', borderRadius: 12, position: 'relative' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
              <span style={{ fontSize: 24 }}>🔒</span>
              <div>
                <h4 style={{ margin: 0, fontSize: 16, color: 'var(--text)' }}>DRE automática Arcane</h4>
                <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>
                  Sobe extrato bancário, planilha ou PDF. O Arcane classifica receitas, custos e despesas, calcula margens e gera alertas. Disponível no plano Profissional+.
                </p>
              </div>
            </div>
            <button className="ad-wizard-submit" onClick={() => navTo('plans')}>Ver plano Profissional</button>
          </div>
        )}

        {isProfissionalPlus && (!dres || dres.length === 0) && (
          <div style={{ marginTop: 16, padding: 24, background: 'rgba(255,255,255,0.02)', border: '1px dashed var(--border)', borderRadius: 12, textAlign: 'center' }}>
            <p style={{ color: 'var(--text-muted)', margin: '0 0 16px' }}>
              Sobe um extrato (CSV/Excel/PDF) e o Arcane monta sua DRE em segundos.
            </p>
            <button className="ad-wizard-submit" onClick={onOpenDreUpload}>+ Subir extrato e gerar DRE</button>
          </div>
        )}

        {isProfissionalPlus && dres && dres.length > 0 && (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 16 }}>
              {dres.slice(0, 6).map((d) => (
                <button
                  key={d.id}
                  onClick={() => onViewDre(d)}
                  style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '14px 16px', background: 'rgba(255,255,255,0.02)',
                    border: '1px solid var(--border)', borderRadius: 8,
                    cursor: 'pointer', textAlign: 'left',
                  }}
                >
                  <div>
                    <div style={{ color: 'var(--text)', fontSize: 14, fontWeight: 500 }}>DRE · {formatMes(d.mes)}</div>
                    <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 2 }}>
                      Receita {formatBRL(d.receita_bruta)} · Lucro {formatBRL(d.lucro_liquido)} · Margem {((Number(d.margem_liquida)||0) * 100).toFixed(1)}%
                    </div>
                  </div>
                  <span style={{ color: 'var(--text-muted)' }}>→</span>
                </button>
              ))}
            </div>
            <div style={{ marginTop: 16, textAlign: 'center' }}>
              <button className="ad-wizard-back" onClick={onOpenDreUpload}>+ Subir nova DRE</button>
            </div>
          </>
        )}
      </div>

      {/* BLOCO 4 — Metas */}
      <div style={{ marginTop: 40 }}>
        <div className="ad-section-divider"><span>Metas</span></div>

        {goalsAtivas.length === 0 && (
          <div style={{ marginTop: 16, padding: 24, background: 'rgba(255,255,255,0.02)', border: '1px dashed var(--border)', borderRadius: 12, textAlign: 'center' }}>
            <p style={{ color: 'var(--text-muted)', margin: '0 0 16px' }}>
              Nenhuma meta ativa. Defina uma meta clara e o Arcane gera um plano de ação pra você.
            </p>
            <button className="ad-wizard-submit" onClick={onOpenGoalForm}>+ Criar primeira meta</button>
          </div>
        )}

        {goalsAtivas.length > 0 && (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 16 }}>
              {goalsAtivas.map((g) => {
                const prog = goalProgress(g);
                return (
                  <div key={g.id} style={{ padding: 20, background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border)', borderRadius: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12, gap: 12, flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: 200 }}>
                        <h4 style={{ margin: 0, fontSize: 16, color: 'var(--text)' }}>{g.titulo}</h4>
                        {g.valor_alvo && (
                          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                            Alvo: {g.tipo === 'faturamento' ? formatBRL(g.valor_alvo) : g.valor_alvo}
                            {g.prazo && <> · Até {new Date(g.prazo).toLocaleDateString('pt-BR')}</>}
                          </div>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {g.plano_ia ? (
                          <button className="ad-wizard-back" onClick={() => onViewGoalPlan(g)}>Ver plano</button>
                        ) : (
                          <button
                            className="ad-wizard-submit"
                            onClick={() => onGenerateGoalPlan(g)}
                            disabled={generatingPlan}
                          >{generatingPlan ? 'Gerando...' : '⚡ Gerar plano Arcane'}</button>
                        )}
                        <button
                          className="ad-wizard-back"
                          onClick={() => onUpdateGoalStatus(g.id, 'concluida')}
                          title="Marcar como concluída"
                        >✓</button>
                        <button
                          className="ad-wizard-back"
                          style={{ color: '#f87171' }}
                          onClick={() => onDeleteGoal(g.id)}
                          title="Remover"
                        >✕</button>
                      </div>
                    </div>

                    {g.valor_alvo && (
                      <div>
                        <div style={{ height: 8, background: 'rgba(255,255,255,0.05)', borderRadius: 4, overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${prog}%`, background: prog >= 100 ? '#10b981' : '#818cf8', transition: 'width 0.4s' }} />
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
                          {prog}% do alvo ({ultimaMetrica ? 'baseado em ' + formatMes(ultimaMetrica.mes) : 'sem métricas ainda'})
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div style={{ marginTop: 16, textAlign: 'center' }}>
              <button className="ad-wizard-back" onClick={onOpenGoalForm}>+ Adicionar meta</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Card simples de stat (Bloco 2)
function StatCard({ label, value }) {
  return (
    <div style={{ padding: 20, background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border)', borderRadius: 12 }}>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 600, color: 'var(--text)' }}>{value}</div>
    </div>
  );
}

// Card de métrica com delta (Bloco 3)
function MetricCard({ label, value, delta }) {
  return (
    <div style={{ padding: 20, background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border)', borderRadius: 12 }}>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 600, color: 'var(--text)' }}>{value}</div>
      {delta !== null && delta !== undefined && !isNaN(delta) && (
        <div style={{ fontSize: 12, marginTop: 6, color: delta >= 0 ? '#10b981' : '#f87171' }}>
          {delta >= 0 ? '↑' : '↓'} {Math.abs(Number(delta) || 0).toFixed(1)}% vs mês anterior
        </div>
      )}
    </div>
  );
}

// Gráfico de barras simples em SVG (sem libs externas) — faturamento dos últimos 6 meses
function FaturamentoChart({ metrics, formatMes, formatBRL }) {
  if (metrics.length < 2) return null;
  const max = Math.max(...metrics.map(m => Number(m.faturamento) || 0), 1);
  const W = 600, H = 160, P = 30;
  const barW = (W - P * 2) / metrics.length - 8;

  return (
    <div style={{ marginTop: 24, padding: 20, background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border)', borderRadius: 12 }}>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 16 }}>
        Evolução do faturamento
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto' }}>
        {metrics.map((m, i) => {
          const v = Number(m.faturamento) || 0;
          const h = (v / max) * (H - P * 2);
          const x = P + i * (barW + 8);
          const y = H - P - h;
          return (
            <g key={m.id || i}>
              <rect x={x} y={y} width={barW} height={h} fill="#818cf8" rx="4" />
              <text x={x + barW/2} y={H - 8} fontSize="10" fill="rgba(255,255,255,0.5)" textAnchor="middle">
                {formatMes(m.mes)}
              </text>
              <text x={x + barW/2} y={y - 6} fontSize="9" fill="rgba(255,255,255,0.7)" textAnchor="middle">
                {formatBRL(v).replace('R$ ', '')}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// Modal: lançar/atualizar métrica do mês
function MetricFormModal({ onClose, onSave, existingMetrics }) {
  const today = new Date();
  const defaultMes = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-01`;
  const [mes, setMes] = useState(defaultMes);
  const [faturamento, setFaturamento] = useState('');
  const [numClientes, setNumClientes] = useState('');
  const [numVendas, setNumVendas] = useState('');
  const [saving, setSaving] = useState(false);

  // Se já existe métrica desse mês, pré-preenche
  useEffect(() => {
    const existing = existingMetrics.find(m => m.mes === mes);
    if (existing) {
      setFaturamento(String(existing.faturamento || ''));
      setNumClientes(String(existing.num_clientes || ''));
      setNumVendas(String(existing.num_vendas || ''));
    } else {
      setFaturamento(''); setNumClientes(''); setNumVendas('');
    }
  }, [mes, existingMetrics]);

  const handleSave = async () => {
    setSaving(true);
    await onSave({ mes, faturamento, num_clientes: numClientes, num_vendas: numVendas });
    setSaving(false);
  };

  return (
    <div className="ad-modal-overlay" onClick={onClose}>
      <div className="ad-modal-box" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <h3 style={{ margin: '0 0 20px', fontSize: 20 }}>Lançar dados do mês</h3>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <label style={{ display: 'block' }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>Mês de referência</div>
            <input type="month" value={mes.slice(0, 7)} onChange={(e) => setMes(e.target.value + '-01')} className="ad-input" />
          </label>
          <label style={{ display: 'block' }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>Faturamento (R$)</div>
            <input type="number" min="0" step="0.01" value={faturamento} onChange={(e) => setFaturamento(e.target.value)} placeholder="ex: 35200" className="ad-input" />
          </label>
          <label style={{ display: 'block' }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>Nº de clientes ativos</div>
            <input type="number" min="0" value={numClientes} onChange={(e) => setNumClientes(e.target.value)} placeholder="ex: 38" className="ad-input" />
          </label>
          <label style={{ display: 'block' }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>Nº de vendas / contratos no mês</div>
            <input type="number" min="0" value={numVendas} onChange={(e) => setNumVendas(e.target.value)} placeholder="ex: 42" className="ad-input" />
          </label>
        </div>

        <div style={{ display: 'flex', gap: 12, marginTop: 24, justifyContent: 'flex-end' }}>
          <button className="ad-wizard-back" onClick={onClose} disabled={saving}>Cancelar</button>
          <button className="ad-wizard-submit" onClick={handleSave} disabled={saving}>{saving ? 'Salvando...' : 'Salvar'}</button>
        </div>
      </div>
    </div>
  );
}

// Modal: criar meta
function GoalFormModal({ onClose, onSave }) {
  const [titulo, setTitulo] = useState('');
  const [tipo, setTipo] = useState('faturamento');
  const [valorAlvo, setValorAlvo] = useState('');
  const [prazo, setPrazo] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!titulo.trim()) return;
    setSaving(true);
    await onSave({ titulo: titulo.trim(), tipo, valor_alvo: valorAlvo, prazo });
    setSaving(false);
  };

  return (
    <div className="ad-modal-overlay" onClick={onClose}>
      <div className="ad-modal-box" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <h3 style={{ margin: '0 0 20px', fontSize: 20 }}>Nova meta</h3>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <label style={{ display: 'block' }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>Título da meta</div>
            <input type="text" value={titulo} onChange={(e) => setTitulo(e.target.value)} placeholder="ex: Faturar R$ 50k/mês" className="ad-input" />
          </label>
          <label style={{ display: 'block' }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>Tipo</div>
            <select value={tipo} onChange={(e) => setTipo(e.target.value)} className="ad-input">
              <option value="faturamento">Faturamento</option>
              <option value="clientes">Nº de clientes</option>
              <option value="vendas">Nº de vendas</option>
              <option value="outro">Outro</option>
            </select>
          </label>
          <label style={{ display: 'block' }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>
              Valor alvo (opcional)
            </div>
            <input type="number" min="0" value={valorAlvo} onChange={(e) => setValorAlvo(e.target.value)} placeholder="ex: 50000" className="ad-input" />
          </label>
          <label style={{ display: 'block' }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>
              Prazo (opcional)
            </div>
            <input type="date" value={prazo} onChange={(e) => setPrazo(e.target.value)} className="ad-input" />
          </label>
        </div>

        <div style={{ display: 'flex', gap: 12, marginTop: 24, justifyContent: 'flex-end' }}>
          <button className="ad-wizard-back" onClick={onClose} disabled={saving}>Cancelar</button>
          <button className="ad-wizard-submit" onClick={handleSave} disabled={saving || !titulo.trim()}>{saving ? 'Salvando...' : 'Criar meta'}</button>
        </div>
      </div>
    </div>
  );
}

// Modal: ver plano Arcane da meta
function GoalPlanModal({ goal, onClose }) {
  return (
    <div className="ad-modal-overlay" onClick={onClose}>
      <div className="ad-modal-box" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 720, maxHeight: '85vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, gap: 12 }}>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>Plano Arcane</div>
            <h3 style={{ margin: 0, fontSize: 20 }}>{goal.titulo}</h3>
          </div>
          <button className="ad-wizard-back" onClick={onClose}>✕</button>
        </div>

        <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.7, color: 'var(--text)', fontSize: 14 }}>
          {goal.plano_ia}
        </div>
      </div>
    </div>
  );
}

// Modal: upload de DRE (CSV/Excel/PDF)
function DreUploadModal({ onClose, onUpload, uploading }) {
  const today = new Date();
  const defaultMes = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-01`;
  const [mes, setMes] = useState(defaultMes);
  const [file, setFile] = useState(null);

  return (
    <div className="ad-modal-overlay" onClick={uploading ? undefined : onClose}>
      <div className="ad-modal-box" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <h3 style={{ margin: '0 0 8px', fontSize: 20 }}>DRE Mensal</h3>
        <p style={{ margin: '0 0 20px', fontSize: 13, color: 'var(--text-muted)' }}>
          Sobe um extrato bancário, planilha ou PDF. O Arcane lê, classifica cada movimento e monta sua DRE estruturada.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <label style={{ display: 'block' }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>Mês de referência</div>
            <input type="month" value={mes.slice(0, 7)} onChange={(e) => setMes(e.target.value + '-01')} className="ad-input" />
          </label>
          <label style={{ display: 'block' }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>Arquivo (CSV, Excel ou PDF)</div>
            <input
              type="file"
              accept=".csv,.xlsx,.xls,.pdf"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="ad-input"
            />
            {file && (
              <div style={{ marginTop: 8, fontSize: 12, color: '#a5b4fc' }}>
                ✓ {file.name} ({(file.size / 1024).toFixed(1)} KB)
              </div>
            )}
          </label>
        </div>

        <div style={{ marginTop: 16, padding: 12, background: 'rgba(99,102,241,0.06)', borderRadius: 8, fontSize: 12, color: 'var(--text-muted)' }}>
          ⏱ O Arcane leva ~30-60s pra processar. Você pode aguardar nessa tela.
        </div>

        <div style={{ display: 'flex', gap: 12, marginTop: 24, justifyContent: 'flex-end' }}>
          <button className="ad-wizard-back" onClick={onClose} disabled={uploading}>Cancelar</button>
          <button
            className="ad-wizard-submit"
            onClick={() => onUpload({ mes, file })}
            disabled={uploading || !file}
          >{uploading ? '⚡ Processando...' : '⚡ Gerar DRE'}</button>
        </div>
      </div>
    </div>
  );
}

// Modal: visualizar DRE detalhada
function DreViewerModal({ dre, onClose }) {
  const formatBRL = (n) => {
    const num = Number(n);
    if (isNaN(num)) return 'R$ 0,00';
    return 'R$ ' + num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };
  const formatPct = (n) => {
    const num = Number(n);
    if (isNaN(num)) return '0%';
    return (num * 100).toFixed(1) + '%';
  };
  const formatMes = (dateStr) => {
    if (!dateStr) return '';
    const [y, m] = dateStr.split('-');
    const meses = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
    return `${meses[parseInt(m)-1]}/${y}`;
  };

  const Linha = ({ label, value, bold = false, sign = null, color }) => (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.05)',
      fontSize: bold ? 15 : 14,
      fontWeight: bold ? 600 : 400,
      color: color || (bold ? 'var(--text)' : 'var(--text-muted)'),
    }}>
      <span>{sign && <span style={{ marginRight: 6, opacity: 0.5 }}>{sign}</span>}{label}</span>
      <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatBRL(value)}</span>
    </div>
  );

  const desp = dre.despesas_operacionais || {};
  const alertas = dre.alertas || [];

  return (
    <div className="ad-modal-overlay" onClick={onClose}>
      <div className="ad-modal-box" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 720, maxHeight: '85vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, gap: 12 }}>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>DRE Mensal</div>
            <h3 style={{ margin: 0, fontSize: 20 }}>{formatMes(dre.mes)}</h3>
            {dre.arquivo_nome && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>Origem: {dre.arquivo_nome}</div>}
          </div>
          <button className="ad-wizard-back" onClick={onClose}>✕</button>
        </div>

        {/* Cards resumo */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 24 }}>
          <div style={{ padding: 16, background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.2)', borderRadius: 10 }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 4 }}>Receita Bruta</div>
            <div style={{ fontSize: 18, fontWeight: 600, color: '#a5b4fc' }}>{formatBRL(dre.receita_bruta)}</div>
          </div>
          <div style={{ padding: 16, background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: 10 }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 4 }}>Lucro Líquido</div>
            <div style={{ fontSize: 18, fontWeight: 600, color: Number(dre.lucro_liquido) >= 0 ? '#10b981' : '#f87171' }}>{formatBRL(dre.lucro_liquido)}</div>
          </div>
          <div style={{ padding: 16, background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border)', borderRadius: 10 }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 4 }}>Margem Bruta</div>
            <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--text)' }}>{formatPct(dre.margem_bruta)}</div>
          </div>
          <div style={{ padding: 16, background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border)', borderRadius: 10 }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 4 }}>Margem Líquida</div>
            <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--text)' }}>{formatPct(dre.margem_liquida)}</div>
          </div>
        </div>

        {/* DRE estruturada */}
        <div style={{ padding: 20, background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border)', borderRadius: 12 }}>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12 }}>
            Demonstração de Resultado
          </div>
          <Linha label="Receita Bruta" value={dre.receita_bruta} sign="+" color="#10b981" />
          <Linha label="(–) Deduções (impostos s/ vendas)" value={dre.deducoes} color="#f87171" />
          <Linha label="= Receita Líquida" value={dre.receita_liquida} bold />
          <Linha label="(–) Custo (CMV)" value={dre.cmv} color="#f87171" />
          <Linha label="= Lucro Bruto" value={dre.lucro_bruto} bold />

          <div style={{ paddingTop: 8, marginTop: 8, fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
            Despesas Operacionais
          </div>
          <Linha label="Pessoal" value={desp.pessoal} sign="–" />
          <Linha label="Marketing" value={desp.marketing} sign="–" />
          <Linha label="Administrativo" value={desp.administrativo} sign="–" />
          <Linha label="Outras" value={desp.outras} sign="–" />

          <Linha label="= EBITDA" value={dre.ebitda} bold />
          <Linha label="(–) Despesas Financeiras" value={dre.despesas_financeiras} color="#f87171" />
          <Linha label="= Lucro Líquido" value={dre.lucro_liquido} bold color={Number(dre.lucro_liquido) >= 0 ? '#10b981' : '#f87171'} />
        </div>

        {/* Alertas */}
        {alertas.length > 0 && (
          <div style={{ marginTop: 20, padding: 16, background: 'rgba(251,191,36,0.06)', border: '1px solid rgba(251,191,36,0.2)', borderRadius: 12 }}>
            <div style={{ fontSize: 11, color: '#fbbf24', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>
              ⚠ Pontos de atenção
            </div>
            <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: 'var(--text)', lineHeight: 1.7 }}>
              {alertas.map((a, i) => <li key={i}>{a}</li>)}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

// Card colapsável com dicas + botão "Preencher com exemplo" pra cada ferramenta
function ToolGuide({ guide, onUseExample }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{
      marginTop: 12,
      padding: '12px 14px',
      background: 'rgba(99,102,241,0.04)',
      border: '1px solid rgba(99,102,241,0.18)',
      borderRadius: 10,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <button
          onClick={() => setOpen(!open)}
          style={{
            background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
            color: '#a5b4fc', fontSize: 13, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6,
          }}
        >
          💡 O que o Arcane precisa saber? <span style={{ fontSize: 10, opacity: 0.6 }}>{open ? '▲' : '▼'}</span>
        </button>
        {guide.exemplo && (
          <button
            onClick={onUseExample}
            style={{
              background: 'rgba(99,102,241,0.12)',
              border: '1px solid rgba(99,102,241,0.3)',
              color: '#a5b4fc',
              fontSize: 12, fontWeight: 500,
              padding: '6px 12px', borderRadius: 6,
              cursor: 'pointer',
            }}
          >
            📋 Preencher com exemplo
          </button>
        )}
      </div>
      {open && guide.dicas && guide.dicas.length > 0 && (
        <ul style={{
          margin: '12px 0 0',
          paddingLeft: 22,
          fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.7,
        }}>
          {guide.dicas.map((d, i) => <li key={i}>{d}</li>)}
        </ul>
      )}
    </div>
  );
}

// ══════════════ DASHBOARD ══════════════
export default function Dashboard({ user, onLogout }) {
  const [tab, setTab] = useState('home');
  const [modId, setModId] = useState(null);
  const [tool, setTool] = useState(null);
  const [input, setInput] = useState('');
  const [fileText, setFileText] = useState('');
  const [fileName, setFileName] = useState('');
  const [fileObj, setFileObj] = useState(null);
  const [result, setResult] = useState('');
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState('');
  const [history, setHistory] = useState([]);
  const [selHist, setSelHist] = useState(null);
  const [profile, setProfile] = useState({ plan: user?.plan || 'free', generations_used: 0, arcane_teste_used: 0 });
  const [drag, setDrag] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Estado do Wizard Arcane Teste
  const [arcaneStep, setArcaneStep] = useState(1);
  const [arcaneData, setArcaneData] = useState({
    setor: '',
    funcionarios: '',
    faturamento: '',
    areas: [],
    descricao: '',
    objetivos: [],
  });
  const [arcaneFile, setArcaneFile] = useState(null);
  const [arcaneLoading, setArcaneLoading] = useState(false);
  const [arcaneResult, setArcaneResult] = useState(null);
  const fileRef = useRef();

  const mod = MODULES.find((m) => m.id === modId);
  const plan = PLANS.find((p) => p.id === (profile.plan || 'free')) || { id: 'free', name: 'Gratuito', limit: 1 };
  const used = profile.generations_used || 0;
  const limit = plan.limit;
  const pct = limit >= 99999 ? 0 : Math.min((used / limit) * 100, 100);
  const left = limit >= 99999 ? 'inf' : Math.max(limit - used, 0);
  const saved = calcSaved(history);

  const loadProfile = useCallback(async () => {
    const { data } = await supabase.from('profiles').select('*').eq('id', user.id).maybeSingle();
    if (data) { setProfile(data); if (!data.profile_complete) setShowModal(true); }
  }, [user.id]);

  const loadHistory = useCallback(async () => {
    const { data } = await supabase.from('history').select('*').eq('user_id', user.id).order('created_at', { ascending: false }).limit(50);
    if (data) setHistory(data);
  }, [user.id]);

  useEffect(() => { loadProfile(); loadHistory(); }, [loadProfile, loadHistory]);

  // ══════════════ Estado Aba Empresa ══════════════
  const [metrics, setMetrics] = useState([]);
  const [goals, setGoals] = useState([]);
  const [dres, setDres] = useState([]);
  const [showMetricForm, setShowMetricForm] = useState(false);
  const [showGoalForm, setShowGoalForm] = useState(false);
  const [showDreUpload, setShowDreUpload] = useState(false);
  const [viewingGoalPlan, setViewingGoalPlan] = useState(null);
  const [viewingDre, setViewingDre] = useState(null);
  const [generatingPlan, setGeneratingPlan] = useState(false);
  const [uploadingDre, setUploadingDre] = useState(false);

  const loadMetrics = useCallback(async () => {
    const { data } = await supabase.from('business_metrics').select('*').eq('user_id', user.id).order('mes', { ascending: false }).limit(12);
    if (data) setMetrics(data);
  }, [user.id]);

  const loadGoals = useCallback(async () => {
    const { data } = await supabase.from('business_goals').select('*').eq('user_id', user.id).order('created_at', { ascending: false });
    if (data) setGoals(data);
  }, [user.id]);

  const loadDres = useCallback(async () => {
    const { data } = await supabase.from('business_dre').select('*').eq('user_id', user.id).order('mes', { ascending: false }).limit(12);
    if (data) setDres(data);
  }, [user.id]);

  useEffect(() => { loadMetrics(); loadGoals(); loadDres(); }, [loadMetrics, loadGoals, loadDres]);

  // ─── Helper: cliente é pago? ───
  const isPaidUser = () => {
    const p = profile.plan;
    return p && p !== 'free' && p !== 'starter';
  };
  const isProfissionalPlus = () => {
    return ['profissional', 'business', 'gestao', 'unlimited'].includes(profile.plan);
  };

  // ─── Bloqueio: ação só pra cliente pago ───
  const requirePaid = (acao = 'usar essa funcionalidade') => {
    if (isPaidUser()) return true;
    setToast(`Pra ${acao} você precisa do plano Essencial+. Veja os planos abaixo.`);
    setTimeout(() => navTo('plans'), 800);
    return false;
  };
  const requireProfissional = (acao = 'usar essa funcionalidade') => {
    if (isProfissionalPlus()) return true;
    setToast(`${acao} é exclusivo do plano Profissional+. Veja os planos.`);
    setTimeout(() => navTo('plans'), 800);
    return false;
  };

  // Salva métrica do mês (cria ou atualiza via upsert lógico)
  const saveMetric = async ({ mes, faturamento, num_clientes, num_vendas }) => {
    const fat = parseFloat(faturamento) || 0;
    const cli = parseInt(num_clientes) || 0;
    const vendas = parseInt(num_vendas) || 0;
    const ticket = vendas > 0 ? fat / vendas : 0;
    // Procura registro existente do mesmo mês pra update; senão insert
    const { data: existing } = await supabase.from('business_metrics').select('id').eq('user_id', user.id).eq('mes', mes).maybeSingle();
    if (existing) {
      const { error } = await supabase.from('business_metrics')
        .update({ faturamento: fat, num_clientes: cli, num_vendas: vendas, ticket_medio: ticket })
        .eq('id', existing.id);
      if (error) { setToast('Erro ao salvar: ' + error.message); return false; }
    } else {
      const { error } = await supabase.from('business_metrics')
        .insert({ user_id: user.id, mes, faturamento: fat, num_clientes: cli, num_vendas: vendas, ticket_medio: ticket });
      if (error) { setToast('Erro ao salvar: ' + error.message); return false; }
    }
    setToast('Métrica salva!');
    await loadMetrics();
    return true;
  };

  // Cria meta nova
  const saveGoal = async ({ titulo, tipo, valor_alvo, prazo }) => {
    const { error } = await supabase.from('business_goals').insert({
      user_id: user.id, titulo,
      tipo: tipo || 'outro',
      valor_alvo: valor_alvo ? parseFloat(valor_alvo) : null,
      prazo: prazo || null,
      status: 'ativa',
    });
    if (error) { setToast('Erro ao criar meta: ' + error.message); return false; }
    setToast('Meta criada!');
    await loadGoals();
    return true;
  };

  // Atualiza status da meta
  const updateGoalStatus = async (goalId, status) => {
    const { error } = await supabase.from('business_goals').update({ status }).eq('id', goalId);
    if (error) { setToast('Erro: ' + error.message); return; }
    await loadGoals();
  };

  // Deleta meta
  const deleteGoal = async (goalId) => {
    if (!window.confirm('Deletar essa meta?')) return;
    const { error } = await supabase.from('business_goals').delete().eq('id', goalId);
    if (error) { setToast('Erro: ' + error.message); return; }
    setToast('Meta removida.');
    await loadGoals();
  };

  // Gera plano via Arcane
  const generateGoalPlan = async (goal) => {
    setGeneratingPlan(true);
    try {
      const response = await fetch(BACKEND_URL + '/modulos/empresa/meta/plano', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.id,
          meta_id: goal.id,
          titulo: goal.titulo,
          tipo: goal.tipo,
          valor_alvo: goal.valor_alvo,
          prazo: goal.prazo,
        }),
      });
      if (!response.ok) {
        const errText = await response.text();
        throw new Error(`Backend ${response.status}: ${errText.slice(0, 200)}`);
      }
      const data = await response.json();
      setViewingGoalPlan({ ...goal, plano_ia: data.plano_ia });
      await loadGoals();
    } catch (err) {
      console.error('Erro ao gerar plano:', err);
      setToast('Erro ao gerar plano. Tenta de novo em alguns segundos.');
    }
    setGeneratingPlan(false);
  };

  // Upload DRE com Arcane (Profissional+)
  const uploadDre = async ({ mes, file }) => {
    if (!file) { setToast('Selecione um arquivo.'); return false; }
    setUploadingDre(true);
    try {
      const fd = new FormData();
      fd.append('user_id', user.id);
      fd.append('mes', mes);
      fd.append('arquivo', file);

      const response = await fetch(BACKEND_URL + '/modulos/empresa/dre/upload', {
        method: 'POST',
        body: fd,
      });
      if (response.status === 403) {
        setToast('DRE Arcane é exclusiva do plano Profissional+.');
        setUploadingDre(false);
        navTo('plans');
        return false;
      }
      if (!response.ok) {
        const errText = await response.text();
        throw new Error(`Backend ${response.status}: ${errText.slice(0, 200)}`);
      }
      const data = await response.json();
      setUploadingDre(false);
      setToast('DRE processada com sucesso!');
      await loadDres();
      await loadMetrics();
      setViewingDre(data.dre);
      return true;
    } catch (err) {
      console.error('Erro ao processar DRE:', err);
      setToast('Erro ao processar DRE. Confira o arquivo e tente de novo.');
      setUploadingDre(false);
      return false;
    }
  };

  const handleFile = async (file) => {
    if (!file) return;
    try {
      setFileObj(file);
      const text = await readFileAsText(file);
      setFileText(text);
      setFileName(file.name);
      setToast(`Arquivo "${file.name}" carregado.`);
    } catch { setToast('Erro ao ler o arquivo.'); }
  };

  const navTo = (t, m = null) => {
    setTab(t); setModId(m); setTool(null); setResult(''); setInput('');
    setFileText(''); setFileName(''); setFileObj(null); setSidebarOpen(false);
  };

  const run = async () => {
    if (!tool) return;
    if (left !== 'inf' && left <= 0) { setToast('Limite atingido. Faça upgrade!'); return; }
    if (!input.trim() && !fileText) { setToast('Descreva o contexto ou envie um arquivo.'); return; }
    setLoading(true); setResult('');
    try {
      let resultText = '';
      const uploadRoute = UPLOAD_ROUTES[tool.id];
      if (uploadRoute && fileObj) {
        const formData = new FormData();
        formData.append('arquivo', fileObj);
        const response = await fetch(BACKEND_URL + uploadRoute, { method: 'POST', body: formData });
        if (!response.ok) { const err = await response.text(); throw new Error(`Backend ${response.status}: ${err.slice(0, 300)}`); }
        const data = await response.json();
        const analise = data.analise_arcane || {};
        const dados = data.dados_processados || {};
        resultText = formatarResultadoModulo(data.modulo, analise, dados);
      } else {
        const payload = {
          tool: tool.id, module: modId, input: input,
          file_content: fileText || undefined, file_name: fileName || undefined,
          company_name: profile.company_name || undefined, sector: profile.sector || undefined,
          company_size: profile.company_size || undefined, tone: profile.tone || undefined,
          city: profile.city || undefined,
        };
        const response = await fetch(BACKEND_URL + '/modulos/texto/gerar', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (!response.ok) { const err = await response.text(); throw new Error(`Backend ${response.status}: ${err.slice(0, 300)}`); }
        const data = await response.json();
        resultText = data.result || data.response || data.text || JSON.stringify(data);
      }
      setResult(resultText);
      await supabase.from('history').insert({
        user_id: user.id, tool: tool.id, module: modId,
        input: (input || fileName || '').substring(0, 500),
        result: resultText.substring(0, 10000),
      });
      await supabase.from('profiles').update({ generations_used: (profile.generations_used || 0) + 1 }).eq('id', user.id);
      setProfile((p) => ({ ...p, generations_used: (p.generations_used || 0) + 1 }));
      loadHistory();
    } catch (err) {
      setResult('Erro ao processar a análise. Tente novamente.\n\n' + (err?.message || err));
    }
    setLoading(false);
  };

  return (
    <div className="arcane-dash">
      <style>{DASH_CSS}</style>
      {showModal && (
        <CompanyProfileModal
          userId={user.id}
          onComplete={(d) => { setShowModal(false); if (d) setProfile((p) => ({ ...p, ...d, profile_complete: true })); }}
        />
      )}
      <button className="ad-mobile-btn" onClick={() => setSidebarOpen(!sidebarOpen)}>
        {sidebarOpen ? '✕' : '☰'}
      </button>

      <div className="ad-root">
        <div className="ad-frame">
          {/* TOPBAR */}
          <div className="ad-topbar">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div className="ad-brand-pill" style={{ margin: 0 }}>
                <span className="ad-brand-square">
                  <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" width="10" height="10">
                    <path d="m3 11 18-5v12L3 14v-3z" />
                  </svg>
                </span>
                <span style={{ fontWeight: 600 }}>Arcane Pro</span>
              </div>

              {/* ABAS DE NAVEGACAO (desktop) */}
              <nav className="ad-topnav">
                <button
                  className={`ad-topnav-item ${tab === 'home' && !modId ? 'active' : ''}`}
                  onClick={() => navTo('home')}
                >Painel</button>
                <button
                  className={`ad-topnav-item ${tab === 'hist' ? 'active' : ''}`}
                  onClick={() => navTo('hist')}
                >Histórico</button>
                <button
                  className={`ad-topnav-item ${tab === 'plans' ? 'active' : ''}`}
                  onClick={() => navTo('plans')}
                >Planos</button>
                <button
                  className={`ad-topnav-item ${tab === 'perfil' ? 'active' : ''}`}
                  onClick={() => navTo('perfil')}
                >Minha Empresa</button>
              </nav>
            </div>
            <div className="ad-topbar-actions">
              <div className="ad-online">
                <span className="ad-online-pulse" />
                <span>Online</span>
              </div>
              <span className="ad-chip-muted">{plan.name}</span>
              {plan.id !== 'gestao' && (
                <button className="ad-btn-primary" onClick={() => navTo('plans')}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="12" height="12"><polyline points="18 15 12 9 6 15" /></svg>
                  Upgrade
                </button>
              )}
              {/* Botão menu mobile (3 pontos) — só aparece em mobile via CSS */}
              <button
                className="ad-mobile-menu-btn"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Menu"
              >
                <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                  <circle cx="12" cy="5" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="12" cy="19" r="2"/>
                </svg>
              </button>
            </div>
          </div>

          {/* DROPDOWN MOBILE — só aparece quando mobileMenuOpen=true */}
          {mobileMenuOpen && (
            <>
              <div
                className="ad-mobile-menu-backdrop"
                onClick={() => setMobileMenuOpen(false)}
              />
              <div className="ad-mobile-menu">
                {tab !== 'home' && (
                  <>
                    <button
                      className="ad-mobile-menu-item"
                      onClick={() => { navTo('home'); setMobileMenuOpen(false); }}
                      style={{ color: 'var(--arcane)', fontWeight: 600 }}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><path d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/></svg>
                      Voltar ao Painel
                    </button>
                    <div style={{ height: 1, background: 'var(--border-soft)', margin: '4px 0' }} />
                  </>
                )}
                <button
                  className="ad-mobile-menu-item"
                  onClick={() => { navTo('hist'); setMobileMenuOpen(false); }}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                  Histórico
                </button>
                <button
                  className="ad-mobile-menu-item"
                  onClick={() => { navTo('plans'); setMobileMenuOpen(false); }}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><path d="M12 2 L2 7 L12 12 L22 7 Z"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg>
                  Planos
                </button>
                <button
                  className="ad-mobile-menu-item"
                  onClick={() => { navTo('perfil'); setMobileMenuOpen(false); }}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                  Minha Empresa
                </button>
                <div style={{ height: 1, background: 'var(--border-soft)', margin: '4px 0' }} />
                <button
                  className="ad-mobile-menu-item"
                  onClick={() => { setMobileMenuOpen(false); onLogout(); }}
                  style={{ color: '#fb7185' }}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                  Sair da conta
                </button>
              </div>
            </>
          )}

          {/* BODY */}
          <div className="ad-body">
            {/* MAIN */}
            <main className="ad-main">
              <div className="ad-main-body">
                {/* HOME */}
                {tab === 'home' && !modId && (
                  <>
                    <div className="ad-hero">
                      <span className="ad-hero-label"><span className="ad-hero-label-dot" />Painel</span>
                      <h2>Bem-vindo ao Arcane</h2>
                      <p>Faça uma pergunta sobre sua empresa, descreva o contexto e receba um diagnóstico estratégico em segundos.</p>
                    </div>

                    {/* CARD ARCANE TESTE - destaque principal */}
                    <button
                      className="ad-arcane-teste-card"
                      onClick={() => navTo('arcane-teste')}
                    >
                      <div className="ad-arcane-teste-glow" />
                      <div className="ad-arcane-teste-content">
                        <div className="ad-arcane-teste-badge">
                          <span className="ad-arcane-teste-badge-dot" />
                          Recomendado
                        </div>
                        <h3 className="ad-arcane-teste-title">Arcane Teste</h3>
                        <p className="ad-arcane-teste-desc">
                          Pergunte qualquer coisa sobre sua empresa. Quanto mais detalhes você der, mais preciso será o diagnóstico.
                        </p>
                        <div className="ad-arcane-teste-cta">
                          Acessar agora
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="14" height="14">
                            <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
                          </svg>
                        </div>
                      </div>
                      <div className="ad-arcane-teste-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="40" height="40">
                          <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
                        </svg>
                      </div>
                    </button>

                    <div className="ad-stats">
                      <div className="ad-stat">
                        <div className="ad-stat-label">Análises usadas</div>
                        <div className="ad-stat-value" style={{ color: '#fff' }}>{used}</div>
                        <div className="ad-stat-sub">{left === 'inf' ? 'Ilimitado' : `de ${limit} no plano`}</div>
                      </div>
                      <div className="ad-stat">
                        <div className="ad-stat-label">Horas economizadas</div>
                        <div className="ad-stat-value" style={{ color: '#10b981' }}>{saved.hours}h</div>
                        <div className="ad-stat-sub">≈ R$ {saved.value} em serviços</div>
                      </div>
                      <div className="ad-stat">
                        <div className="ad-stat-label">Seu plano</div>
                        <div className="ad-stat-value" style={{ color: '#a5b4fc' }}>{plan.name}</div>
                        <div className="ad-stat-sub">{plan.price}{plan.period}</div>
                      </div>
                    </div>

                    <div className="ad-section-divider">
                      <span>Outros módulos</span>
                    </div>

                    <div className="ad-mods-grid">
                      {MODULES.map((m) => {
                        const locked = !canUse(profile.plan, m.plan);
                        return (
                          <button
                            key={m.id}
                            className="ad-modcard"
                            onClick={() => {
                              if (locked) { setToast(`Requer plano ${m.plan.toUpperCase()}.`); navTo('plans'); return; }
                              navTo('mod', m.id);
                            }}
                          >
                            <div className="ad-modcard-head">
                              <div className="ad-modcard-icon" style={{ background: `${m.color}14`, border: `1px solid ${m.color}33`, color: m.color }}>
                                {m.icon}
                              </div>
                              <PlanTag plan={m.plan} />
                            </div>
                            <h3>{m.label}</h3>
                            <p>{m.desc}</p>
                            {locked && <div className="ad-modcard-locked">🔒 Requer upgrade</div>}
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}

                {/* LISTA DE TOOLS */}
                {tab === 'mod' && modId && !tool && (
                  <>
                    <div className="ad-crumb">
                      <button onClick={() => navTo('home')}>← Painel</button>
                      <span className="ad-crumb-sep">|</span>
                      <span className="ad-crumb-current">{mod?.label}</span>
                    </div>
                    <div className="ad-hero">
                      <span className="ad-hero-label"><span className="ad-hero-label-dot" style={{ background: mod?.color }} />Módulo</span>
                      <h2>{mod?.label}</h2>
                      <p>{mod?.desc}</p>
                    </div>
                    <div>
                      {mod?.tools.map((t) => {
                        const locked = !canUse(profile.plan, mod.plan);
                        return (
                          <button
                            key={t.id}
                            className="ad-tool-row"
                            disabled={locked}
                            onClick={() => {
                              if (locked) { setToast(`Requer plano ${mod.plan.toUpperCase()}.`); navTo('plans'); return; }
                              setTool(t);
                            }}
                          >
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div className="name">{t.name}</div>
                              <div className="desc">{t.desc}</div>
                            </div>
                            <div className="side">
                              <span className="saved">~{Math.round((TOOL_TIME[t.id] || 60) / 60 * 10) / 10}h ec.</span>
                              {t.upload && <span className="upload-tag">UPLOAD</span>}
                              {locked ? <span>🔒</span> : <span className="chev">→</span>}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}

                {/* TOOL FORM */}
                {tab === 'mod' && modId && tool && (
                  <>
                    <div className="ad-crumb">
                      <button onClick={() => setTool(null)}>← {mod?.label}</button>
                      <span className="ad-crumb-sep">|</span>
                      <span className="ad-crumb-current">{tool.name}</span>
                    </div>
                    <div className="ad-hero">
                      <span className="ad-hero-label"><span className="ad-hero-label-dot" />Ferramenta</span>
                      <h2>{tool.name}</h2>
                      <p>{tool.desc}</p>
                      {profile.company_name && (
                        <div style={{
                          display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 12,
                          padding: '5px 12px', background: 'rgba(99,102,241,0.08)',
                          border: '1px solid rgba(99,102,241,0.2)', borderRadius: 999,
                          fontSize: 11, color: '#a5b4fc', position: 'relative', zIndex: 1,
                        }}>
                          ✓ Personalizado para {profile.company_name}
                        </div>
                      )}
                    </div>
                    <div className="ad-toolform">
                      <div>
                        {tool.upload && (
                          <div style={{ marginBottom: 14 }}>
                            <FieldLabel>Arquivo (opcional)</FieldLabel>
                            <div
                              className={`ad-upload${drag ? ' drag' : ''}`}
                              onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
                              onDragLeave={() => setDrag(false)}
                              onDrop={(e) => { e.preventDefault(); setDrag(false); handleFile(e.dataTransfer.files[0]); }}
                              onClick={() => fileRef.current?.click()}
                            >
                              <input
                                ref={fileRef} type="file"
                                accept=".txt,.csv,.xlsx,.xls,.pdf"
                                style={{ display: 'none' }}
                                onChange={(e) => handleFile(e.target.files[0])}
                              />
                              {fileName ? (
                                <>
                                  <div className="ad-upload-file">✓ {fileName}</div>
                                  <div className="ad-upload-sub">Clique para trocar</div>
                                  <button
                                    type="button"
                                    className="ad-upload-remove"
                                    onClick={(e) => { e.stopPropagation(); setFileText(''); setFileName(''); setFileObj(null); }}
                                  >Remover</button>
                                </>
                              ) : (
                                <>
                                  <div style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 6 }}>Arraste o arquivo ou clique para selecionar</div>
                                  <div className="ad-upload-sub">Suporta: .txt, .csv, .xlsx, .pdf</div>
                                </>
                              )}
                            </div>
                          </div>
                        )}
                        {(() => {
                          const guide = TOOL_GUIDES[tool.id] || {};
                          const placeholder = guide.placeholder || (tool.upload ? 'Descreva o contexto do arquivo...' : 'Descreva com detalhes a situação...');
                          return (
                            <>
                              <FieldLabel>{tool.upload ? 'Contexto adicional' : 'Descreva o cenário'}</FieldLabel>
                              <textarea
                                className="ad-textarea" value={input}
                                onChange={(e) => setInput(e.target.value)}
                                placeholder={placeholder}
                                rows={tool.upload ? 5 : 10}
                              />
                              {(guide.dicas || guide.exemplo) && (
                                <ToolGuide
                                  guide={guide}
                                  onUseExample={() => { setInput(guide.exemplo || ''); setToast('Exemplo carregado. Edite com seus dados.'); }}
                                />
                              )}
                            </>
                          );
                        })()}
                        <button
                          className="ad-execute" onClick={run}
                          disabled={loading || (!input.trim() && !fileText)}
                        >
                          {loading ? 'Processando análise...' : 'Executar análise'}
                        </button>
                        {loading && (
                          <div style={{ marginTop: 10, fontSize: 12, color: 'var(--text-dim)', display: 'flex', gap: 8, alignItems: 'center' }}>
                            <span style={{ color: 'var(--arcane)' }}>▶</span>Analisando dados...
                          </div>
                        )}
                      </div>
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                          <FieldLabel>Resultado da Análise</FieldLabel>
                          {result && (
                            <button
                              onClick={() => { navigator.clipboard.writeText(result); setToast('Copiado!'); }}
                              style={{ background: 'transparent', border: 'none', color: '#a5b4fc', fontSize: 11, padding: 0, letterSpacing: '0.1em', fontWeight: 600 }}
                            >COPIAR</button>
                          )}
                        </div>
                        <div className="ad-result">
                          {result || <span style={{ color: 'var(--text-dim)' }}>O resultado aparecerá aqui.</span>}
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* ARCANE TESTE - placeholder, vamos preencher depois */}
                {tab === 'arcane-teste' && (
                  <>
                    <div className="ad-crumb">
                      <button onClick={() => navTo('home')}>← Painel</button>
                      <span className="ad-crumb-sep">|</span>
                      <span className="ad-crumb-current">Arcane Teste</span>
                    </div>

                    {!arcaneResult && !arcaneLoading && (
                      <>
                        <div className="ad-hero">
                          <span className="ad-hero-label">
                            <span className="ad-hero-label-dot" />Arcane Teste · Passo {arcaneStep} de 4
                          </span>
                          <h2>{
                            arcaneStep === 1 ? 'Conta sobre sua empresa' :
                            arcaneStep === 2 ? 'Onde você quer melhorar?' :
                            arcaneStep === 3 ? 'Conte mais sobre essa dor' :
                            'O que você espera receber?'
                          }</h2>
                          <p>{
                            arcaneStep === 1 ? 'Dados básicos pra eu entender o contexto. Só clica nas opções abaixo.' :
                            arcaneStep === 2 ? 'Pode escolher mais de uma. Quanto mais áreas, mais completa a análise.' :
                            arcaneStep === 3 ? 'Quanto mais detalhe você der aqui, mais preciso será o diagnóstico.' :
                            'Pode marcar quantas opções quiser. Isso me ajuda a focar a análise.'
                          }</p>
                        </div>

                        {/* Barra de progresso */}
                        <div className="ad-wizard-progress">
                          {[1,2,3,4].map(n => (
                            <div key={n} className={`ad-wizard-step ${arcaneStep >= n ? 'done' : ''} ${arcaneStep === n ? 'current' : ''}`}>
                              <div className="ad-wizard-step-num">{arcaneStep > n ? '✓' : n}</div>
                              <div className="ad-wizard-step-label">
                                {n === 1 ? 'Empresa' : n === 2 ? 'Áreas' : n === 3 ? 'Contexto' : 'Objetivo'}
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* PASSO 1 — Empresa */}
                        {arcaneStep === 1 && (
                          <div className="ad-wizard-card">
                            <div className="ad-wizard-q">
                              <label className="ad-wizard-label">Qual o setor da empresa?</label>
                              <div className="ad-chip-grid">
                                {['Comércio', 'Serviços', 'Indústria', 'Saúde', 'Educação', 'Beleza', 'Alimentação', 'Tecnologia', 'Outro'].map(s => (
                                  <button
                                    key={s}
                                    className={`ad-chip ${arcaneData.setor === s ? 'selected' : ''}`}
                                    onClick={() => setArcaneData({...arcaneData, setor: s})}
                                  >{s}</button>
                                ))}
                              </div>
                            </div>

                            <div className="ad-wizard-q">
                              <label className="ad-wizard-label">Quantos funcionários?</label>
                              <div className="ad-chip-grid">
                                {['Sou sozinho', '2-5', '6-10', '11-50', '50+'].map(s => (
                                  <button
                                    key={s}
                                    className={`ad-chip ${arcaneData.funcionarios === s ? 'selected' : ''}`}
                                    onClick={() => setArcaneData({...arcaneData, funcionarios: s})}
                                  >{s}</button>
                                ))}
                              </div>
                            </div>

                            <div className="ad-wizard-q">
                              <label className="ad-wizard-label">Faturamento mensal aproximado?</label>
                              <div className="ad-chip-grid">
                                {['Até 10k', '10-50k', '50-200k', '200k-1M', '1M+', 'Prefiro não dizer'].map(s => (
                                  <button
                                    key={s}
                                    className={`ad-chip ${arcaneData.faturamento === s ? 'selected' : ''}`}
                                    onClick={() => setArcaneData({...arcaneData, faturamento: s})}
                                  >{s}</button>
                                ))}
                              </div>
                            </div>

                            <div className="ad-wizard-actions">
                              <div />
                              <button
                                className="ad-wizard-next"
                                disabled={!arcaneData.setor || !arcaneData.funcionarios || !arcaneData.faturamento}
                                onClick={() => setArcaneStep(2)}
                              >
                                Próximo passo
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="14" height="14"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                              </button>
                            </div>
                          </div>
                        )}

                        {/* PASSO 2 — Áreas */}
                        {arcaneStep === 2 && (
                          <div className="ad-wizard-card">
                            <div className="ad-area-grid">
                              {[
                                { id: 'fin', icon: '💰', label: 'Financeiro', dor: 'Meu caixa tá apertado, não sei pra onde vai o dinheiro' },
                                { id: 'rh', icon: '👥', label: 'RH/Pessoas', dor: 'Tenho dificuldade com equipe, contratação ou demissão' },
                                { id: 'mkt', icon: '📈', label: 'Marketing/Vendas', dor: 'Tô investindo em anúncios mas não sei se dá retorno' },
                                { id: 'ops', icon: '⚙️', label: 'Operações', dor: 'Os processos são bagunçados, perco tempo' },
                                { id: 'jur', icon: '⚖️', label: 'Jurídico', dor: 'Tenho dúvidas em contratos ou leis' },
                                { id: 'est', icon: '🎯', label: 'Estratégia', dor: 'Não sei pra onde a empresa deveria ir' },
                              ].map(a => (
                                <button
                                  key={a.id}
                                  className={`ad-area-card ${arcaneData.areas.includes(a.id) ? 'selected' : ''}`}
                                  onClick={() => {
                                    const has = arcaneData.areas.includes(a.id);
                                    setArcaneData({...arcaneData, areas: has ? arcaneData.areas.filter(x => x !== a.id) : [...arcaneData.areas, a.id]});
                                  }}
                                >
                                  <div className="ad-area-icon">{a.icon}</div>
                                  <div className="ad-area-content">
                                    <div className="ad-area-label">{a.label}</div>
                                    <div className="ad-area-dor">"{a.dor}"</div>
                                  </div>
                                  <div className="ad-area-check">{arcaneData.areas.includes(a.id) ? '✓' : ''}</div>
                                </button>
                              ))}
                            </div>

                            <div className="ad-wizard-actions">
                              <button className="ad-wizard-back" onClick={() => setArcaneStep(1)}>← Voltar</button>
                              <button
                                className="ad-wizard-next"
                                disabled={arcaneData.areas.length === 0}
                                onClick={() => setArcaneStep(3)}
                              >
                                Próximo passo
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="14" height="14"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                              </button>
                            </div>
                          </div>
                        )}

                        {/* PASSO 3 — Contexto (textarea + indicador qualidade) */}
                        {arcaneStep === 3 && (() => {
                          const exampleByArea = {
                            fin: 'Tenho um comércio há 3 anos, mas mês passado o caixa não fechou. Acho que tô gastando muito com fornecedor X mas não sei como cortar sem perder qualidade. Estou pensando em demitir 1 funcionário mas tenho medo de prejudicar as vendas.',
                            rh: 'Tenho 8 funcionários e 2 estão sempre faltando. Não sei se demito ou converso. Outro problema: minha melhor vendedora pediu aumento de 30% e não sei se aceito.',
                            mkt: 'Gasto R$3 mil por mês em anúncios no Instagram e Google. Vendo umas 20 unidades por mês com isso. Não sei se vale a pena ou se tô jogando dinheiro fora. CAC parece alto mas não tenho certeza.',
                            ops: 'Cada cliente que entra demora muito pra ser atendido. Não temos sistema, anotamos tudo no caderno. Já perdi vendas porque cliente foi embora cansado de esperar.',
                            jur: 'Tenho um contrato com fornecedor que tá há 2 anos sem revisar. Ele aumentou o preço do nada e não sei se posso quebrar o contrato sem multa.',
                            est: 'Estou faturando 50k/mês há 1 ano e não consigo crescer. Não sei se contrato mais gente, abro filial, ou foco em vender online. Tenho medo de errar a próxima decisão.',
                          };
                          const example = exampleByArea[arcaneData.areas[0]] || exampleByArea.fin;

                          // Calcula qualidade do prompt (heurística simples)
                          const txt = arcaneData.descricao;
                          const hasNumbers = /\d/.test(txt);
                          const hasLength = txt.length > 100;
                          const hasGoodLength = txt.length > 250;
                          const score = Math.min(100, (txt.length / 4) + (hasNumbers ? 20 : 0) + (hasGoodLength ? 20 : 0));
                          const scoreLabel = score < 30 ? 'Pouco detalhe' : score < 60 ? 'Razoável' : score < 80 ? 'Bom' : 'Excelente';
                          const scoreColor = score < 30 ? '#fb7185' : score < 60 ? '#fbbf24' : score < 80 ? '#a5b4fc' : '#34d399';

                          return (
                            <div className="ad-wizard-card">
                              <textarea
                                className="ad-wizard-textarea"
                                placeholder={`Exemplo: ${example}`}
                                value={arcaneData.descricao}
                                onChange={(e) => setArcaneData({...arcaneData, descricao: e.target.value})}
                                rows={8}
                              />

                              <div className="ad-wizard-quality">
                                <div className="ad-wizard-quality-bar">
                                  <div className="ad-wizard-quality-fill" style={{ width: `${score}%`, background: scoreColor }} />
                                </div>
                                <div className="ad-wizard-quality-info">
                                  <span style={{ color: scoreColor, fontWeight: 600 }}>{scoreLabel}</span>
                                  <span className="ad-wizard-quality-tip">
                                    {score < 30 ? '💡 Dica: descreva o problema com mais detalhes' :
                                     score < 60 ? '💡 Dica: adicione números, datas ou nomes específicos' :
                                     score < 80 ? '✓ Tá bom! Pode adicionar mais contexto se quiser' :
                                     '✓ Excelente! Sua análise vai ser muito precisa'}
                                  </span>
                                </div>
                              </div>

                              <button
                                className="ad-wizard-use-example"
                                onClick={() => setArcaneData({...arcaneData, descricao: example})}
                              >
                                ↓ Usar este exemplo como base
                              </button>

                              <div className="ad-wizard-actions">
                                <button className="ad-wizard-back" onClick={() => setArcaneStep(2)}>← Voltar</button>
                                <button
                                  className="ad-wizard-next"
                                  disabled={arcaneData.descricao.length < 30}
                                  onClick={() => setArcaneStep(4)}
                                >
                                  Próximo passo
                                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="14" height="14"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                                </button>
                              </div>
                            </div>
                          );
                        })()}

                        {/* PASSO 4 — Objetivos */}
                        {arcaneStep === 4 && (
                          <div className="ad-wizard-card">
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                              {[
                                'Diagnóstico claro do que está errado',
                                'Plano de ação com passos concretos',
                                'Comparação com outras empresas do setor',
                                'Identificação de oportunidades escondidas',
                                'Estimativa de impacto financeiro (R$)',
                              ].map(o => (
                                <button
                                  key={o}
                                  className={`ad-objective ${arcaneData.objetivos.includes(o) ? 'selected' : ''}`}
                                  onClick={() => {
                                    const has = arcaneData.objetivos.includes(o);
                                    setArcaneData({...arcaneData, objetivos: has ? arcaneData.objetivos.filter(x => x !== o) : [...arcaneData.objetivos, o]});
                                  }}
                                >
                                  <div className="ad-objective-check">{arcaneData.objetivos.includes(o) ? '✓' : ''}</div>
                                  <span>{o}</span>
                                </button>
                              ))}
                            </div>

                            {/* Upload opcional — CSV / Excel / PDF */}
                            <div style={{ marginTop: 24, padding: 16, border: '1px dashed var(--border)', borderRadius: 12 }}>
                              <div style={{ fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>
                                Anexar dados (opcional)
                              </div>
                              <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 12px' }}>
                                Anexe uma planilha (CSV, Excel) ou PDF com seus dados — extrato, vendas, fluxo de caixa.
                                A análise fica muito mais precisa com números reais.
                              </p>
                              <input
                                type="file"
                                accept=".csv,.xlsx,.xls,.pdf"
                                onChange={(e) => setArcaneFile(e.target.files?.[0] || null)}
                                style={{ fontSize: 13, color: 'var(--text)', width: '100%' }}
                              />
                              {arcaneFile && (
                                <div style={{ marginTop: 8, fontSize: 12, color: '#a5b4fc' }}>
                                  ✓ {arcaneFile.name} ({(arcaneFile.size / 1024).toFixed(1)} KB)
                                  <button
                                    onClick={() => setArcaneFile(null)}
                                    style={{ marginLeft: 12, background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 12, textDecoration: 'underline' }}
                                  >Remover</button>
                                </div>
                              )}
                            </div>

                            <div className="ad-wizard-actions">
                              <button className="ad-wizard-back" onClick={() => setArcaneStep(3)}>← Voltar</button>
                              <button
                                className="ad-wizard-submit"
                                disabled={arcaneData.objetivos.length === 0}
                                onClick={async () => {
                                  // Verifica limite local antes de chamar (UX rápido + economiza chamada)
                                  const usadoLocal = profile.arcane_teste_used || 0;
                                  if ((profile.plan === 'free' || !profile.plan) && usadoLocal >= 3) {
                                    setArcaneResult({ esgotado: true });
                                    return;
                                  }
                                  setArcaneLoading(true);
                                  try {
                                    const fd = new FormData();
                                    fd.append('user_id', user.id);
                                    fd.append('setor', arcaneData.setor);
                                    fd.append('funcionarios', arcaneData.funcionarios);
                                    fd.append('faturamento', arcaneData.faturamento);
                                    fd.append('areas', JSON.stringify(arcaneData.areas));
                                    fd.append('descricao', arcaneData.descricao);
                                    fd.append('objetivos', JSON.stringify(arcaneData.objetivos));
                                    if (arcaneFile) fd.append('arquivo', arcaneFile);

                                    const response = await fetch(BACKEND_URL + '/modulos/arcane-teste/analisar', {
                                      method: 'POST',
                                      body: fd,
                                    });
                                    if (response.status === 403) {
                                      // Backend confirmou que esgotou
                                      setArcaneLoading(false);
                                      setArcaneResult({ esgotado: true });
                                      return;
                                    }
                                    if (!response.ok) {
                                      const errText = await response.text();
                                      throw new Error(`Backend ${response.status}: ${errText.slice(0, 200)}`);
                                    }
                                    const data = await response.json();
                                    setArcaneLoading(false);
                                    setArcaneResult({
                                      analysis_id: data.analysis_id,
                                      analise: data.analise,
                                    });
                                    // Atualiza contador local com o valor do backend
                                    if (typeof data.arcane_teste_used === 'number') {
                                      setProfile((p) => ({ ...p, arcane_teste_used: data.arcane_teste_used }));
                                    }
                                  } catch (err) {
                                    console.error('Erro ao gerar análise Arcane Teste:', err);
                                    setArcaneLoading(false);
                                    setToast('Erro ao gerar análise. Tenta de novo em alguns segundos.');
                                  }
                                }}
                              >
                                ⚡ Gerar análise
                              </button>
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    {/* LOADING */}
                    {arcaneLoading && (
                      <div className="ad-wizard-loading">
                        <div className="ad-wizard-loading-icon">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="40" height="40">
                            <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
                          </svg>
                        </div>
                        <h3>Analisando seu negócio...</h3>
                        <p>Aguarde alguns segundos enquanto cruzamos seus dados com benchmarks do setor.</p>
                        <div className="ad-wizard-loading-steps">
                          <div className="done">✓ Recebendo seus dados</div>
                          <div className="done">✓ Identificando contexto: {arcaneData.setor}</div>
                          <div className="loading">○ Analisando áreas selecionadas</div>
                          <div>○ Cruzando com benchmarks do setor</div>
                          <div>○ Gerando recomendações</div>
                        </div>
                      </div>
                    )}

                    {/* RESULTADO — análise completa, sem paywall */}
                    {arcaneResult && !arcaneResult.esgotado && (
                      <div className="ad-wizard-result">
                        <div className="ad-hero">
                          <span className="ad-hero-label"><span className="ad-hero-label-dot" />Análise pronta</span>
                          <h2>Diagnóstico Arcane</h2>
                          <p>
                            Análise completa do seu negócio.
                            {(profile.plan === 'free' || !profile.plan) && (
                              <> Você usou <b>{profile.arcane_teste_used || 0} de 3</b> análises gratuitas.</>
                            )}
                          </p>
                        </div>

                        <div className="ad-result-preview" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
                          {arcaneResult.analise}
                        </div>

                        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginTop: 24, flexWrap: 'wrap' }}>
                          <button
                            className="ad-wizard-submit"
                            style={{ flex: '0 1 auto' }}
                            onClick={() => {
                              setArcaneResult(null);
                              setArcaneStep(1);
                              setArcaneData({setor: '', funcionarios: '', faturamento: '', areas: [], descricao: '', objetivos: []});
                              setArcaneFile(null);
                            }}
                          >Fazer outra análise</button>
                          {(profile.plan === 'free' || !profile.plan) && (
                            <button
                              className="ad-wizard-back"
                              style={{ flex: '0 1 auto' }}
                              onClick={() => navTo('plans')}
                            >Ver planos completos</button>
                          )}
                        </div>
                      </div>
                    )}

                    {/* ESGOTADO — usuário free atingiu o limite de 3 análises */}
                    {arcaneResult && arcaneResult.esgotado && (
                      <div className="ad-wizard-result">
                        <div className="ad-hero" style={{ textAlign: 'center' }}>
                          <span className="ad-hero-label"><span className="ad-hero-label-dot" />Limite atingido</span>
                          <h2>Você já conheceu o Arcane 🎯</h2>
                          <p style={{ maxWidth: 560, margin: '0 auto' }}>
                            Suas <b>3 análises gratuitas</b> foram usadas. Pra continuar gerando diagnósticos,
                            acessar todos os 6 módulos e ter histórico completo, escolha um plano.
                          </p>
                        </div>
                        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginTop: 32, flexWrap: 'wrap' }}>
                          <button
                            className="ad-wizard-submit"
                            onClick={() => navTo('plans')}
                          >Ver planos a partir de R$ 127</button>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* MINHA EMPRESA — Hub completo */}
                {tab === 'perfil' && (
                  <EmpresaHub
                    user={user}
                    profile={profile}
                    metrics={metrics}
                    goals={goals}
                    dres={dres}
                    history={history}
                    saved={saved}
                    isPaidUser={isPaidUser()}
                    isProfissionalPlus={isProfissionalPlus()}
                    onOpenMetricForm={() => { if (requirePaid('lançar métricas')) setShowMetricForm(true); }}
                    onOpenGoalForm={() => { if (requirePaid('criar metas')) setShowGoalForm(true); }}
                    onOpenDreUpload={() => { if (requireProfissional('DRE Arcane')) setShowDreUpload(true); }}
                    onViewGoalPlan={(g) => setViewingGoalPlan(g)}
                    onViewDre={(d) => setViewingDre(d)}
                    onGenerateGoalPlan={(g) => { if (requirePaid('gerar plano Arcane')) generateGoalPlan(g); }}
                    onUpdateGoalStatus={updateGoalStatus}
                    onDeleteGoal={deleteGoal}
                    generatingPlan={generatingPlan}
                    onSaveProfile={(d) => setProfile((p) => ({ ...p, ...d }))}
                    setToast={setToast}
                    navTo={navTo}
                  />
                )}

                {/* Modal: lançar métrica do mês */}
                {showMetricForm && (
                  <MetricFormModal
                    onClose={() => setShowMetricForm(false)}
                    onSave={async (data) => {
                      const ok = await saveMetric(data);
                      if (ok) setShowMetricForm(false);
                    }}
                    existingMetrics={metrics}
                  />
                )}

                {/* Modal: criar meta */}
                {showGoalForm && (
                  <GoalFormModal
                    onClose={() => setShowGoalForm(false)}
                    onSave={async (data) => {
                      const ok = await saveGoal(data);
                      if (ok) setShowGoalForm(false);
                    }}
                  />
                )}

                {/* Modal: ver plano Arcane da meta */}
                {viewingGoalPlan && (
                  <GoalPlanModal
                    goal={viewingGoalPlan}
                    onClose={() => setViewingGoalPlan(null)}
                  />
                )}

                {/* Modal: upload DRE */}
                {showDreUpload && (
                  <DreUploadModal
                    onClose={() => setShowDreUpload(false)}
                    onUpload={async (data) => {
                      const ok = await uploadDre(data);
                      if (ok) setShowDreUpload(false);
                    }}
                    uploading={uploadingDre}
                  />
                )}

                {/* Modal: ver DRE detalhada */}
                {viewingDre && (
                  <DreViewerModal
                    dre={viewingDre}
                    onClose={() => setViewingDre(null)}
                  />
                )}

                {/* PLANS */}
                {tab === 'plans' && (
                  <>
                    <div className="ad-hero">
                      <span className="ad-hero-label"><span className="ad-hero-label-dot" />Planos</span>
                      <h2>Gerencie seu plano</h2>
                      <p>{profile.plan === 'free' || !profile.plan
                        ? <>Você está no plano <b style={{ color: '#a5b4fc' }}>Gratuito</b>. Faça upgrade para desbloquear todos os módulos.</>
                        : <>Você está no plano <b style={{ color: '#a5b4fc' }}>{plan.name}</b>.</>}
                      </p>
                    </div>
                    <div className="ad-plans-grid">
                      {PLANS.map((p) => (
                        <div key={p.id} className={`ad-plancard ${p.id === profile.plan ? 'current' : ''} ${p.highlight ? 'highlight' : ''}`}>
                          {p.highlight && <div className="ad-plancard-tag">Mais escolhido</div>}
                          {p.id === profile.plan && <div className="ad-plancard-current">ATUAL</div>}
                          <div className="ad-plancard-name">{p.name}</div>
                          <div className="ad-plancard-price">
                            {p.price}<span className="period">{p.period}</span>
                          </div>
                          <div className="ad-plancard-feats">
                            {p.features.map((f, i) => (
                              <div key={i} className="ad-plancard-feat">
                                <span className="check">✓</span>{f}
                              </div>
                            ))}
                          </div>
                          <button
                            className={`ad-plancard-btn ${p.highlight ? '' : 'ghost'}`}
                            onClick={() => {
                              if (p.id === profile.plan) return;
                              if (p.id === 'gestao') {
                                // Enterprise: contato direto, sem checkout
                                window.open(ENTERPRISE_MAILTO, '_blank');
                                return;
                              }
                              window.open(MP_LINKS[p.id], '_blank');
                            }}
                            disabled={p.id === profile.plan}
                          >
                            {p.id === profile.plan ? 'Plano atual'
                              : p.id === 'essencial' ? 'Assinar Essencial'
                              : p.id === 'profissional' ? 'Desbloquear Profissional'
                              : p.id === 'gestao' ? 'Falar com vendas'
                              : 'Assinar'}
                          </button>
                          {p.id !== profile.plan && p.id !== 'gestao' && (
                            <div style={{ textAlign: 'center', marginTop: 6, fontSize: 10, color: 'var(--text-dim)' }}>
                              PIX · Cartão · Boleto
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {/* HISTORICO */}
                {tab === 'hist' && (
                  <>
                    <div className="ad-hero">
                      <span className="ad-hero-label"><span className="ad-hero-label-dot" />Histórico</span>
                      <h2>Suas análises recentes</h2>
                      {saved.hours > 0 && <p>Você economizou ~{saved.hours}h (R$ {saved.value} em serviços).</p>}
                    </div>
                    {history.length === 0 ? (
                      <div style={{ border: '1px solid var(--border-soft)', background: 'var(--panel)', borderRadius: 14, padding: 40, textAlign: 'center', color: 'var(--text-dim)', fontSize: 14 }}>
                        Nenhuma análise realizada ainda. Use um módulo para começar.
                      </div>
                    ) : (
                      <div className={`ad-hist-grid ${selHist ? 'has-detail' : ''}`} style={{ display: 'grid', gridTemplateColumns: selHist ? '320px 1fr' : '1fr', gap: 14 }}>
                        <div className="ad-hist-list">
                          {history.map((h) => (
                            <button
                              key={h.id}
                              className="ad-tool-row"
                              onClick={() => setSelHist(selHist?.id === h.id ? null : h)}
                              style={{ borderColor: selHist?.id === h.id ? 'rgba(99,102,241,0.35)' : undefined }}
                            >
                              <div style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--arcane)', flexShrink: 0 }} />
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                                  <span style={{ fontSize: 13, fontWeight: 500 }}>{h.tool?.replace(/_/g, ' ')}</span>
                                  <span style={{ fontSize: 11, color: '#34d399' }}>+{Math.round((TOOL_TIME[h.tool] || 60) / 60 * 10) / 10}h</span>
                                </div>
                                <div style={{ fontSize: 12, color: 'var(--text-dim)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h.input}</div>
                              </div>
                            </button>
                          ))}
                        </div>
                        {selHist && (
                          <div className="ad-hist-detail" style={{ border: '1px solid var(--border-soft)', background: 'var(--panel)', borderRadius: 14, padding: 18, position: 'sticky', top: 80, maxHeight: '70vh', overflow: 'auto' }}>
                            <button
                              className="ad-hist-back-btn"
                              onClick={() => setSelHist(null)}
                            >
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><polyline points="15 18 9 12 15 6"/></svg>
                              Voltar à lista
                            </button>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                              <div style={{ fontSize: 14, fontWeight: 500 }}>{selHist.tool?.replace(/_/g, ' ')}</div>
                              <button
                                onClick={() => { navigator.clipboard.writeText(selHist.result); setToast('Copiado!'); }}
                                style={{ background: 'transparent', border: 'none', color: '#a5b4fc', fontSize: 11, padding: 0, fontWeight: 600 }}
                              >COPIAR</button>
                            </div>
                            <div style={{ fontSize: 12, color: 'var(--text-dim)', fontStyle: 'italic', marginBottom: 10 }}>{selHist.input}</div>
                            <div style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{selHist.result}</div>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            </main>

            {/* SIDEBAR DIR */}
            <aside className="ad-side ad-side-right">
              <div className="ad-side-head">
                <div className="ad-side-title">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="14" height="14">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                  Sua conta
                </div>
              </div>
              <div className="ad-panel">
                <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                  <div className="ad-avatar">{(profile.company_name || user?.name || user?.email || 'U')[0].toUpperCase()}</div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 15, color: 'var(--text)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 2 }}>
                      {profile.company_name || user?.name || user?.email?.split('@')[0] || 'Usuário'}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-dim)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {user?.email}
                    </div>
                  </div>
                </div>
                <button className="ad-btn-ghost ad-btn-ghost-small" onClick={() => navTo('perfil')}>
                  Editar empresa
                </button>
                <button
                  className="ad-btn-ghost ad-btn-ghost-small"
                  onClick={onLogout}
                  style={{ color: '#fb7185', borderColor: 'rgba(244,63,94,0.2)' }}
                >
                  ← Sair da conta
                </button>
              </div>
              <div className="ad-panel">
                <div className="ad-panel-head">
                  <span>Plano atual</span>
                  <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#fff', background: 'var(--arcane)', padding: '3px 7px', borderRadius: 5 }}>
                    {plan.name}
                  </span>
                </div>
                {plan.id !== 'gestao' ? (
                  <>
                    <p style={{ fontSize: 11, color: 'var(--text-dim)', margin: '0 0 10px' }}>
                      Desbloqueie mais análises e módulos avançados.
                    </p>
                    <button className="ad-btn-ghost ad-btn-ghost-small" onClick={() => navTo('plans')}>
                      Ver planos
                    </button>
                  </>
                ) : (
                  <p style={{ fontSize: 11, color: 'var(--text-dim)', margin: 0 }}>
                    Você tem acesso ilimitado a todos os módulos.
                  </p>
                )}
              </div>
              <div className="ad-panel">
                <div className="ad-panel-head">
                  <span>Uso no mês</span>
                  <span className="ad-panel-badge">{used} / {left === 'inf' ? '∞' : limit}</span>
                </div>
                <div className="ad-usage-bar">
                  <div className="ad-usage-fill" style={{ width: `${pct}%` }} />
                </div>
                <div style={{ fontSize: 10, color: 'var(--text-dim)', marginTop: 8 }}>
                  {left === 'inf' ? 'Análises ilimitadas' : `${left} restantes neste mês`}
                </div>
              </div>
              {saved.hours > 0 && (
                <div className="ad-panel">
                  <div className="ad-panel-head"><span>Economia estimada</span></div>
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 22, fontWeight: 700, color: '#10b981' }}>
                    {saved.hours}h
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>
                    ≈ R$ {saved.value} em consultorias
                  </div>
                </div>
              )}
            </aside>
          </div>
        </div>
      </div>
      {toast && <Toast msg={toast} onClose={() => setToast('')} />}
    </div>
  );
}

/* ============================================================
 *  INTEGRAÇÃO NO App.js
 *  ------------------------------------------------------------
 *  1) Coloque este arquivo em: src/Dashboard.jsx
 *
 *  2) No App.js, adicione no topo (após o import do supabase):
 *       import Dashboard from './Dashboard';
 *
 *  3) A chamada no render continua a mesma:
 *       <Dashboard user={user} onLogout={handleLogout} />
 *
 *  4) Você pode DELETAR do App.js:
 *     - a função Dashboard antiga
 *     - CompanyProfileModal (agora está dentro deste arquivo)
 *     - ProfileEditor (agora está dentro deste arquivo)
 *     - UPLOAD_ROUTES, MODULES, TOOL_TIME, PLANS, PLAN_ORDER, KIRVANO_LINKS,
 *       MP_LINKS, SECTORS, SIZES, TONES, HOURLY_RATE (todos duplicados aqui)
 *     - canUse, formatarResultadoModulo, readFileAsText, calcSaved (duplicados)
 *     - BACKEND_URL (duplicado)
 *
 *   Mas se preferir, pode simplesmente MANTER as duplicatas no App.js
 *   (elas não vão causar conflito, só ocupam linhas).
 * ============================================================ */